package ds.pms.tokafull.adminweb

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.colspan
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.border
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontFamily
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.marginLeft
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.minWidth
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.CheckboxInput
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.jetbrains.compose.web.renderComposable
import org.w3c.dom.HTMLTableCellElement
import kotlin.js.Date
import kotlinx.browser.window

private val Bg = org.jetbrains.compose.web.css.Color("#F4F6FB")
private val Surface = org.jetbrains.compose.web.css.Color("#FFFFFF")
/** Sample dashboard accent — indigo / violet. */
private val Primary = org.jetbrains.compose.web.css.Color("#5D5FEF")
private val PrimaryDark = org.jetbrains.compose.web.css.Color("#4F46E5")
private val PrimarySoft = org.jetbrains.compose.web.css.Color("#EEF0FF")
private val AccentCoral = org.jetbrains.compose.web.css.Color("#FF708B")
private val AccentOrange = org.jetbrains.compose.web.css.Color("#FF9F43")
private val AccentGreen = org.jetbrains.compose.web.css.Color("#20C997")
private val Slate900 = org.jetbrains.compose.web.css.Color("#1E293B")
private val Slate700 = org.jetbrains.compose.web.css.Color("#475569")
private val Slate500 = org.jetbrains.compose.web.css.Color("#64748B")
private val Slate400 = org.jetbrains.compose.web.css.Color("#94A3B8")
private val Slate200 = org.jetbrains.compose.web.css.Color("#E8ECF4")
private val Border = org.jetbrains.compose.web.css.Color("#E2E8F0")
private val Danger = org.jetbrains.compose.web.css.Color("#E11D48")
private val SuccessBg = org.jetbrains.compose.web.css.Color("#D1FAE5")

private const val FontStack = "'Plus Jakarta Sans', 'Segoe UI', system-ui, sans-serif"
private const val PrimaryGradient = "linear-gradient(135deg, #5D5FEF 0%, #8B5CF6 100%)"
private const val SoftCardShadow = "0 8px 24px rgba(30, 41, 59, 0.06)"
private const val LiftCardShadow = "0 16px 40px rgba(30, 41, 59, 0.10)"

/** Agent accounts are mobile-app users; Admin accounts manage this dashboard. */
private val USER_TYPE_SELECT_OPTIONS = listOf("admin" to "Admin", "agent" to "App user")
private val USER_STATUS_SELECT_OPTIONS = listOf("active" to "Active", "inactive" to "Inactive")

private fun truncUserCell(s: String?, max: Int = 32): String {
    val t = s?.trim().orEmpty()
    if (t.isEmpty()) return "—"
    return if (t.length <= max) t else "${t.take(max)}…"
}

private fun userTypeLabel(raw: String): String = when (raw.trim().lowercase()) {
    "agent" -> "App user"
    "admin" -> "Admin"
    else -> raw.replaceFirstChar { it.uppercaseChar() }
}

/** Resolve product image URLs so thumbnails work when admin UI is on :8081 and media on API :8080. */
private fun resolveProductImageSrc(raw: String?): String? {
    val u = raw?.trim()?.takeIf { it.isNotEmpty() } ?: return null
    if (u.startsWith("http://", ignoreCase = true) ||
        u.startsWith("https://", ignoreCase = true) ||
        u.startsWith("data:", ignoreCase = true) ||
        u.startsWith("blob:", ignoreCase = true)
    ) {
        return u
    }
    val configured = runCatching { js("globalThis.ADMIN_API_BASE_URL") as? String }.getOrNull()
        ?.trim()?.trimEnd('/').orEmpty()
    val base = configured.ifEmpty {
        runCatching {
            val host = window.location.hostname
            val proto = window.location.protocol
            if (host == "127.0.0.1" || host.equals("localhost", ignoreCase = true)) {
                "$proto//$host:8080"
            } else {
                ""
            }
        }.getOrDefault("")
    }
    if (base.isEmpty()) return u
    return if (u.startsWith("/")) "$base$u" else "$base/$u"
}

fun main() {
    val viewModel = AdminViewModel()
    renderComposable(rootElementId = "root") {
        LaunchedEffect(Unit) { viewModel.bootstrap() }
        val state by viewModel.uiState.collectAsState()
        if (!state.authReady) {
            Div({
                style {
                    property("min-height", "100vh")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    property("justify-content", "center")
                    backgroundColor(Bg)
                }
            }) {
                P({ style { color(Slate500) } }) { Text("Loading…") }
            }
            return@renderComposable
        }
        if (!state.signedIn) {
            LoginScreen(viewModel, state)
        } else {
            AppShell(viewModel, state)
        }
    }
}

@Composable
private fun LoginScreen(vm: AdminViewModel, state: AdminUiState) {
    var user by remember { mutableStateOf("") }
    var pass by remember { mutableStateOf("") }
    Div({
        style {
            property("min-height", "100vh")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            property("justify-content", "center")
            backgroundColor(Bg)
            fontFamily("Plus Jakarta Sans", "Segoe UI", "system-ui", "sans-serif")
            padding(24.px)
        }
    }) {
        Div({
            style {
                width(420.px)
                property("max-width", "100%")
                backgroundColor(Surface)
                borderRadius(20.px)
                padding(40.px)
                border(1.px, LineStyle.Solid, Slate200)
                property("box-shadow", LiftCardShadow)
            }
        }) {
            Div({
                style {
                    width(44.px)
                    height(44.px)
                    borderRadius(12.px)
                    property("background", PrimaryGradient)
                    marginBottom(16.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    property("justify-content", "center")
                    color(org.jetbrains.compose.web.css.Color.white)
                    fontWeight("800")
                    fontSize(18.px)
                }
            }) { Text("T") }
            H1({
                style {
                    color(Slate900)
                    fontSize(24.px)
                    margin(0.px)
                    marginBottom(8.px)
                    fontWeight("800")
                }
            }) { Text("Toka PMS") }
            P({ style { color(Slate500); margin(0.px); marginBottom(28.px); fontSize(14.px) } }) {
                Text("Sign in to the operations dashboard")
            }
            if (state.error != null) {
                P({
                    style {
                        color(Danger)
                        fontSize(13.px)
                        marginBottom(16.px)
                        fontWeight("600")
                    }
                }) { Text(state.error ?: "") }
            }
            P({ style { fontSize(12.px); fontWeight("700"); color(Slate700); marginBottom(6.px) } }) { Text("Username") }
            Input(InputType.Text, attrs = {
                value(user)
                onInput { user = it.value }
                placeholder("admin")
                style {
                    width(100.percent)
                    padding(12.px)
                    borderRadius(10.px)
                    border(1.px, LineStyle.Solid, Border)
                    marginBottom(16.px)
                    fontSize(15.px)
                }
            })
            P({ style { fontSize(12.px); fontWeight("700"); color(Slate700); marginBottom(6.px) } }) { Text("Password") }
            Input(InputType.Password, attrs = {
                value(pass)
                onInput { pass = it.value }
                placeholder("••••••••")
                style {
                    width(100.percent)
                    padding(12.px)
                    borderRadius(10.px)
                    border(1.px, LineStyle.Solid, Border)
                    marginBottom(24.px)
                    fontSize(15.px)
                }
            })
            Button(attrs = {
                style {
                    width(100.percent)
                    padding(14.px)
                    borderRadius(12.px)
                    border(0.px, LineStyle.None, Border)
                    property("background", PrimaryGradient)
                    color(org.jetbrains.compose.web.css.Color.white)
                    fontWeight("700")
                    fontSize(15.px)
                    property("cursor", "pointer")
                    property("box-shadow", "0 8px 20px rgba(93, 95, 239, 0.35)")
                }
                if (state.loading) disabled()
                onClick { vm.login(user, pass) }
            }) { Text(if (state.loading) "Signing in…" else "Sign in") }
        }
    }
}

@Composable
private fun AppShell(vm: AdminViewModel, state: AdminUiState) {
    Div({
        style {
            property("min-height", "100vh")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            fontFamily("Plus Jakarta Sans", "Segoe UI", "system-ui", "sans-serif")
            backgroundColor(Bg)
        }
    }) {
        AppHeader(state, vm)
        Div({
            style {
                flex(1)
                display(DisplayStyle.Flex)
                gap(0.px)
                minHeight(0.px)
            }
        }) {
            SideNav(state.tab, vm::setTab)
            Div({
                style {
                    flex(1)
                    padding(24.px)
                    property("overflow", "auto")
                    backgroundColor(Bg)
                }
            }) {
                MainCard(state, vm)
            }
        }
        AppFooter()
    }
}

@Composable
private fun AppHeader(state: AdminUiState, vm: AdminViewModel) {
    Div({
        style {
            backgroundColor(Surface)
            color(Slate900)
            padding(14.px, 28.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            property("border-bottom", "1px solid #E8ECF4")
            property("box-shadow", "0 1px 0 rgba(30,41,59,0.04)")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
                flex(1)
            }
        }) {
            Div({
                style {
                    width(36.px)
                    height(36.px)
                    borderRadius(10.px)
                    property("background", PrimaryGradient)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    property("justify-content", "center")
                    color(org.jetbrains.compose.web.css.Color.white)
                    fontWeight("800")
                    fontSize(14.px)
                }
            }) { Text("T") }
            Div {
                H2({
                    style {
                        margin(0.px)
                        fontSize(17.px)
                        fontWeight("800")
                        color(Slate900)
                    }
                }) { Text("Toka Product Management") }
                P({
                    style {
                        margin(0.px)
                        marginTop(2.px)
                        fontSize(12.px)
                        color(Slate500)
                    }
                }) { Text("Catalog · Pricing · Access control") }
            }
        }
        Span({
            style {
                fontSize(13.px)
                property("margin-right", "16px")
                color(Slate700)
                fontWeight("600")
            }
        }) { Text(state.sessionUsername ?: "") }
        Button(attrs = {
            attr("type", "button")
            style {
                padding(8.px, 16.px)
                borderRadius(10.px)
                border(1.px, LineStyle.Solid, Border)
                backgroundColor(Surface)
                color(Slate700)
                fontWeight("600")
                fontSize(13.px)
                property("cursor", "pointer")
            }
            onClick { vm.logout() }
        }) { Text("Sign out") }
    }
}

@Composable
private fun AppFooter() {
    Div({
        style {
            property("border-top", "1px solid #E2E8F0")
            padding(14.px, 28.px)
            backgroundColor(Surface)
            display(DisplayStyle.Flex)
            property("justify-content", "center")
            property("flex-wrap", "wrap")
            gap(12.px)
        }
    }) {
        P({
            style {
                margin(0.px)
                fontSize(12.px)
                color(Slate500)
                flex(1)
            }
        }) { Text("© ${jsYear()} Toka · Internal use · Support: IT / Operations") }
        P({
            style {
                margin(0.px)
                fontSize(12.px)
                color(Slate500)
            }
        }) { Text("v1.0 · PostgreSQL + Ktor") }
    }
}

private fun jsYear(): String =
    Date().getFullYear().toInt().toString()

private fun formatEpochMs(ms: Long): String =
    if (ms <= 0L) "—" else Date(ms.toDouble()).toLocaleString()

private val deliveryMonthAbbr = listOf(
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
)

/** yyyy-MM-dd → "14 Oct, 2026" */
private fun isoDateToDisplay(iso: String): String {
    val m = Regex("""^(\d{4})-(\d{2})-(\d{2})$""").matchEntire(iso.trim()) ?: return iso.trim()
    val (y, mo, d) = m.destructured
    val monthIdx = mo.toIntOrNull()?.minus(1) ?: return iso.trim()
    if (monthIdx !in 0..11) return iso.trim()
    val day = d.toIntOrNull() ?: return iso.trim()
    return "$day ${deliveryMonthAbbr[monthIdx]}, $y"
}

/** Build stored delivery label from ISO from/to (keeps app-friendly range text). */
private fun formatEstimatedDeliveryLabel(fromIso: String, toIso: String): String {
    val from = fromIso.trim()
    val to = toIso.trim()
    if (from.isEmpty() && to.isEmpty()) return ""
    if (to.isEmpty()) return isoDateToDisplay(from)
    if (from.isEmpty()) return isoDateToDisplay(to)
    val fm = Regex("""^(\d{4})-(\d{2})-(\d{2})$""").matchEntire(from)
    val tm = Regex("""^(\d{4})-(\d{2})-(\d{2})$""").matchEntire(to)
    if (fm != null && tm != null) {
        val (fy, fmo, fd) = fm.destructured
        val (ty, tmo, td) = tm.destructured
        val fMi = fmo.toIntOrNull()?.minus(1) ?: return "${isoDateToDisplay(from)} - ${isoDateToDisplay(to)}"
        val tMi = tmo.toIntOrNull()?.minus(1) ?: return "${isoDateToDisplay(from)} - ${isoDateToDisplay(to)}"
        if (fMi !in 0..11 || tMi !in 0..11) return "${isoDateToDisplay(from)} - ${isoDateToDisplay(to)}"
        val fDay = fd.toIntOrNull() ?: return "${isoDateToDisplay(from)} - ${isoDateToDisplay(to)}"
        val tDay = td.toIntOrNull() ?: return "${isoDateToDisplay(from)} - ${isoDateToDisplay(to)}"
        return if (fy == ty) {
            "$fDay ${deliveryMonthAbbr[fMi]} - $tDay ${deliveryMonthAbbr[tMi]}, $fy"
        } else {
            "${isoDateToDisplay(from)} - ${isoDateToDisplay(to)}"
        }
    }
    return "${isoDateToDisplay(from)} - ${isoDateToDisplay(to)}"
}

private fun monthNameToIndex(name: String): Int? {
    val n = name.trim().lowercase()
    deliveryMonthAbbr.forEachIndexed { i, abbr ->
        if (abbr.lowercase() == n || abbr.lowercase().startsWith(n.take(3))) return i
    }
    return null
}

private fun partsToIso(day: String, monthName: String, year: String): String? {
    val d = day.toIntOrNull() ?: return null
    val mi = monthNameToIndex(monthName) ?: return null
    val y = year.toIntOrNull() ?: return null
    if (d !in 1..31 || y !in 2000..2100) return null
    return "$y-${(mi + 1).toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
}

/**
 * Parse stored delivery text into ISO from/to for date inputs.
 * Supports: "14 Oct - 18 Oct, 2026", "14 Oct, 2026", "2026-10-14", "2026-10-14 - 2026-10-18".
 */
private fun parseEstimatedDeliveryToIsoPair(raw: String): Pair<String, String> {
    val t = raw.trim()
    if (t.isEmpty()) return "" to ""
    Regex("""^(\d{4}-\d{2}-\d{2})\s*[-–to]+\s*(\d{4}-\d{2}-\d{2})$""", RegexOption.IGNORE_CASE)
        .matchEntire(t)
        ?.let { return it.groupValues[1] to it.groupValues[2] }
    Regex("""^(\d{4}-\d{2}-\d{2})$""").matchEntire(t)?.let { return it.groupValues[1] to "" }
    Regex(
        """^(\d{1,2})\s+([A-Za-z]{3,9})\s*[-–]\s*(\d{1,2})\s+([A-Za-z]{3,9})\s*,?\s*(\d{4})$""",
        RegexOption.IGNORE_CASE,
    ).matchEntire(t)?.let { m ->
        val from = partsToIso(m.groupValues[1], m.groupValues[2], m.groupValues[5]) ?: return "" to ""
        val to = partsToIso(m.groupValues[3], m.groupValues[4], m.groupValues[5]) ?: return from to ""
        return from to to
    }
    Regex(
        """^(\d{1,2})\s+([A-Za-z]{3,9})\s*,?\s*(\d{4})$""",
        RegexOption.IGNORE_CASE,
    ).matchEntire(t)?.let { m ->
        val from = partsToIso(m.groupValues[1], m.groupValues[2], m.groupValues[3]) ?: return "" to ""
        return from to ""
    }
    return "" to ""
}

private fun htmlEscape(s: String): String = buildString(s.length + 8) {
    for (c in s) {
        when (c) {
            '<' -> append("&lt;")
            '>' -> append("&gt;")
            '&' -> append("&amp;")
            '"' -> append("&quot;")
            else -> append(c)
        }
    }
}

private fun orderAgentNameText(detail: OrderDetailDto): String =
    detail.agentName?.trim()?.takeIf { it.isNotEmpty() } ?: "—"

private fun orderAgentContactText(detail: OrderDetailDto): String =
    detail.agentContact?.trim()?.takeIf { it.isNotEmpty() } ?: "—"

private fun triggerOrderPrint(detail: OrderDetailDto) {
    val statusLabel =
        ADMIN_ORDER_STATUS_OPTIONS.find { it.first == detail.status.lowercase() }?.second ?: detail.status
    val agentName = orderAgentNameText(detail)
    val agentContact = orderAgentContactText(detail)
    val agentUr = detail.agentNameUr?.trim()?.takeIf { it.isNotEmpty() }
    val isReprint = detail.printCount > 0
    val lines = if (detail.lines.isNotEmpty()) {
        detail.lines
    } else {
        listOf(
            OrderLineItemDto(
                productName = detail.productName,
                productNameUr = detail.productNameUr,
                brandCode = detail.brandCode,
                skuCode = detail.skuCode,
                quantity = detail.quantity,
                totalPrice = detail.totalPrice
            )
        )
    }
    val itemsTable = buildString {
        append("<table class=\"lines\">")
        append("<thead><tr>")
        listOf("Product", "Brand", "SKU", "Qty", "Line total").forEach { h ->
            append("<th>")
            append(htmlEscape(h))
            append("</th>")
        }
        append("</tr></thead><tbody>")
        lines.forEach { line ->
            val ur = line.productNameUr?.trim()?.takeIf { it.isNotEmpty() }
            val nameCell = buildString {
                append("<div class=\"pname\">")
                append(htmlEscape(line.productName))
                append("</div>")
                if (ur != null) {
                    append("<div class=\"purl\">")
                    append(htmlEscape(ur))
                    append("</div>")
                }
            }
            append("<tr>")
            append("<td>$nameCell</td>")
            append("<td><span class=\"pill\">${htmlEscape(line.brandCode ?: "—")}</span></td>")
            append("<td class=\"mono\">${htmlEscape(line.skuCode ?: "—")}</td>")
            append("<td class=\"qty\">${line.quantity}</td>")
            append("<td class=\"money\">${htmlEscape(line.totalPrice)}</td>")
            append("</tr>")
        }
        append("</tbody></table>")
    }
    val reprintBanner =
        if (isReprint) {
            """
            <div class="banner warn">
              <strong>REPRINT</strong>
              — previously printed ${detail.printCount} time(s)${
                detail.printedAtMs?.let { " · last: ${htmlEscape(formatEpochMs(it))}" } ?: ""
            }
            </div>
            """.trimIndent()
        } else {
            ""
        }
    val deliveryRow =
        if (detail.estimatedDelivery.isNotBlank()) {
            """<div class="meta-row"><span>Est. delivery</span><strong>${htmlEscape(detail.estimatedDelivery)}</strong></div>"""
        } else {
            ""
        }
    val agentCell = buildString {
        append(htmlEscape(agentName))
        if (agentUr != null) {
            append("<div class=\"purl\">")
            append(htmlEscape(agentUr))
            append("</div>")
        }
    }
    val printedAt = htmlEscape(formatEpochMs(Date().getTime().toLong()))
    val html = """
        <!DOCTYPE html><html><head><meta charset="utf-8"/>
        <title>Order ${htmlEscape(detail.orderId)}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com"/>
        <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap" rel="stylesheet"/>
        <style>
          :root {
            --ink:#1E293B; --muted:#64748B; --line:#E8ECF4; --bg:#F4F6FB;
            --brand:#5D5FEF; --brand2:#8B5CF6; --surface:#fff;
          }
          * { box-sizing: border-box; }
          body {
            margin: 0; padding: 28px;
            font-family: 'Plus Jakarta Sans', Segoe UI, system-ui, sans-serif;
            color: var(--ink); background: var(--bg);
          }
          .sheet {
            max-width: 880px; margin: 0 auto; background: var(--surface);
            border-radius: 18px; border: 1px solid var(--line);
            box-shadow: 0 12px 32px rgba(30,41,59,.08); overflow: hidden;
          }
          .head {
            padding: 22px 28px;
            background: linear-gradient(135deg, #5D5FEF 0%, #8B5CF6 100%);
            color: #fff; display: flex; justify-content: space-between; gap: 16px; align-items: flex-start;
          }
          .brand-mark {
            width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,.2);
            display: inline-flex; align-items: center; justify-content: center;
            font-weight: 800; margin-right: 12px; vertical-align: middle;
          }
          .head h1 { margin: 0; font-size: 22px; font-weight: 800; letter-spacing: -0.02em; }
          .head .sub { margin: 6px 0 0; opacity: .9; font-size: 12px; font-weight: 600; }
          .head .meta-right { text-align: right; font-size: 12px; opacity: .95; line-height: 1.5; }
          .body { padding: 24px 28px 28px; }
          .banner {
            margin: 0 0 16px; padding: 10px 14px; border-radius: 10px; font-size: 13px;
          }
          .banner.warn { background: #FFF4E5; color: #9A3412; border: 1px solid #FED7AA; }
          .section-title {
            margin: 0 0 10px; font-size: 12px; font-weight: 800; letter-spacing: .06em;
            text-transform: uppercase; color: var(--muted);
          }
          table.lines { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 20px; }
          table.lines th {
            text-align: left; padding: 10px 12px; font-size: 11px; letter-spacing: .04em;
            text-transform: uppercase; color: var(--muted); background: #F8FAFC;
            border-bottom: 1px solid var(--line); font-weight: 700;
          }
          table.lines td {
            padding: 12px; border-bottom: 1px solid var(--line); vertical-align: top;
          }
          table.lines tr:last-child td { border-bottom: none; }
          .pname { font-weight: 700; color: var(--ink); }
          .purl { color: var(--muted); font-size: 12px; direction: rtl; text-align: end; margin-top: 4px; }
          .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 12px; color: #475569; word-break: break-all; }
          .qty, .money { font-weight: 700; white-space: nowrap; }
          .pill {
            display: inline-block; padding: 2px 8px; border-radius: 999px;
            background: #EEF0FF; color: #4338CA; font-weight: 700; font-size: 11px;
          }
          .meta-grid {
            display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
          }
          .meta-row {
            background: #F8FAFC; border: 1px solid var(--line); border-radius: 12px;
            padding: 12px 14px; display: flex; flex-direction: column; gap: 4px;
          }
          .meta-row span { font-size: 11px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: .04em; }
          .meta-row strong { font-size: 14px; font-weight: 700; color: var(--ink); }
          .total-bar {
            margin-top: 16px; padding: 14px 16px; border-radius: 12px;
            background: linear-gradient(135deg, rgba(93,95,239,.08), rgba(139,92,246,.10));
            border: 1px solid #E0E7FF; display: flex; justify-content: space-between; align-items: center;
          }
          .total-bar .label { font-size: 12px; font-weight: 700; color: var(--muted); text-transform: uppercase; }
          .total-bar .value { font-size: 18px; font-weight: 800; color: #4338CA; }
          .foot {
            margin-top: 18px; padding-top: 12px; border-top: 1px dashed var(--line);
            font-size: 11px; color: var(--muted); display: flex; justify-content: space-between; gap: 12px;
          }
          @media print {
            body { background: #fff; padding: 0; }
            .sheet { box-shadow: none; border: none; border-radius: 0; max-width: none; }
            .head { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .pill, .meta-row, .total-bar, table.lines th, .banner {
              -webkit-print-color-adjust: exact; print-color-adjust: exact;
            }
          }
        </style></head><body>
        <div class="sheet">
          <div class="head">
            <div>
              <span class="brand-mark">T</span>
              <h1 style="display:inline-block;vertical-align:middle">Order ${htmlEscape(detail.orderId)}</h1>
              <p class="sub">Toka Product Management · Sales order</p>
            </div>
            <div class="meta-right">
              <div>Printed ${printedAt}</div>
              <div>Status: ${htmlEscape(statusLabel)}</div>
            </div>
          </div>
          <div class="body">
            $reprintBanner
            <p class="section-title">Line items</p>
            $itemsTable
            <p class="section-title">Order details</p>
            <div class="meta-grid">
              <div class="meta-row"><span>Agent</span><strong>$agentCell</strong></div>
              <div class="meta-row"><span>Contact</span><strong>${htmlEscape(agentContact)}</strong></div>
              <div class="meta-row"><span>Status</span><strong>${htmlEscape(statusLabel)}</strong></div>
              <div class="meta-row"><span>Created</span><strong>${htmlEscape(formatEpochMs(detail.createdAtMs))}</strong></div>
              $deliveryRow
            </div>
            <div class="total-bar">
              <div class="label">Order total · ${detail.quantity} item(s)</div>
              <div class="value">${htmlEscape(detail.totalPrice)}</div>
            </div>
            <div class="foot">
              <span>Toka · Internal document</span>
              <span>${htmlEscape(detail.orderId)}</span>
            </div>
          </div>
        </div>
        <script>window.onload=function(){window.print();};</script>
        </body></html>
    """.trimIndent()
    val w = window.open("", "_blank") ?: return
    w.document?.open()
    w.document?.write(html)
    w.document?.close()
}

private fun printOrderFromAdmin(detail: OrderDetailDto, vm: AdminViewModel) {
    val alreadyPrinted = detail.printCount > 0
    if (alreadyPrinted) {
        val whenText = detail.printedAtMs?.let { formatEpochMs(it) } ?: "earlier"
        val ok = window.confirm(
            "This order was already printed ($whenText), print count ${detail.printCount}.\n\nPrint again (reprint)?"
        )
        if (!ok) return
    }
    triggerOrderPrint(detail)
    vm.markOrderPrintedAfterPrint(detail.orderId, wasReprint = alreadyPrinted)
}

@Composable
private fun MainCard(state: AdminUiState, vm: AdminViewModel) {
    Div({
        style {
            backgroundColor(Surface)
            border(1.px, LineStyle.Solid, Slate200)
            borderRadius(20.px)
            padding(24.px)
            minHeight(480.px)
            property("box-shadow", SoftCardShadow)
        }
    }) {
        PageToolbar(state, vm)
        StatusStrip(state)
        when (state.tab) {
            AdminTab.Products -> ProductsTab(state, vm)
            AdminTab.Variants -> VariantsSearchTab(state, vm)
            AdminTab.Matrix -> MatrixTab(state, vm)
            AdminTab.Modifiers -> ModifiersTab(state, vm)
            AdminTab.Orders -> OrdersTab(state, vm)
            AdminTab.Users -> UsersTab(state, vm)
        }
        state.orderDetail?.let { detail -> OrderDetailOverlay(detail, vm) }
        state.editUserForm?.let { form -> UserEditOverlay(form, state, vm) }
        if (!state.loading) AuditTrail(state)
    }
}

@Composable
private fun PageToolbar(state: AdminUiState, vm: AdminViewModel) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.FlexStart)
            property("justify-content", "space-between")
            marginBottom(20.px)
            property("flex-wrap", "wrap")
            gap(12.px)
        }
    }) {
        Div {
            H3({ style { color(Slate900); margin(0.px); marginBottom(6.px); fontSize(20.px) } }) {
                Text(
                    when (state.tab) {
                        AdminTab.Products -> "Product Catalogue"
                        AdminTab.Variants -> "Variants search"
                        AdminTab.Matrix -> "Pricing matrix"
                        AdminTab.Modifiers -> "Price modifiers"
                        AdminTab.Orders -> "Sales orders"
                        AdminTab.Users -> "User accounts"
                    }
                )
            }
            P({ style { color(Slate500); margin(0.px); fontSize(13.px) } }) {
                Text(
                    when (state.tab) {
                        AdminTab.Products -> "Browse products — results update as you type in the search box."
                        AdminTab.Variants -> "Type to filter variants live by name or SKU. Clear the box to show all variants."
                        AdminTab.Matrix -> "Edit brand base prices. Bulk tools apply to the current page only."
                        AdminTab.Modifiers -> "Attribute-based price adjustments used by the pricing engine."
                        AdminTab.Orders -> "Orders placed from the mobile app. Update status, delivery date, and print receipts."
                        AdminTab.Users -> "Admin and App users (mobile login). Create with type App user for the shop app. You cannot delete your own account."
                    }
                )
            }
        }
        PrimaryButton("Refresh data", enabled = !state.loading) { vm.refreshAll() }
    }
}

@Composable
private fun StatusStrip(state: AdminUiState) {
    if (state.loading) P({ style { color(Slate700); marginBottom(12.px); fontSize(13.px) } }) { Text("Loading…") }
    if (state.saving) P({ style { color(Primary); marginBottom(12.px); fontSize(13.px) } }) { Text("Saving…") }
    if (state.error != null) P({ style { color(Danger); marginBottom(12.px); fontWeight("700"); fontSize(13.px) } }) { Text(state.error ?: "") }
}

@Composable
private fun PaginationRow(
    page: Int,
    pageSize: Int,
    total: Long,
    onPrev: () -> Unit,
    onNext: () -> Unit,
    onPage: (Int) -> Unit
) {
    val totalPages = kotlin.math.max(1L, (total + pageSize - 1) / pageSize).toInt()
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(10.px)
            marginTop(16.px)
            marginBottom(8.px)
            property("flex-wrap", "wrap")
        }
    }) {
        GhostButton("Previous", enabled = page > 1) { onPrev() }
        Span({ style { color(Slate700); fontSize(13.px) } }) {
            Text("Page $page of $totalPages · $total rows")
        }
        GhostButton("Next", enabled = page < totalPages) { onNext() }
    }
}

@Composable
private fun SideNav(current: AdminTab, onSelect: (AdminTab) -> Unit) {
    Div({
        style {
            width(240.px)
            backgroundColor(Surface)
            property("border-right", "1px solid #E8ECF4")
            padding(18.px, 16.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(6.px)
        }
    }) {
        P({
            style {
                color(Slate400)
                fontSize(11.px)
                fontWeight("700")
                marginBottom(8.px)
                property("letter-spacing", "0.06em")
            }
        }) { Text("NAVIGATION") }
        NavItem("Products", current == AdminTab.Products) { onSelect(AdminTab.Products) }
        NavItem("Variants", current == AdminTab.Variants) { onSelect(AdminTab.Variants) }
        NavItem("Pricing matrix", current == AdminTab.Matrix) { onSelect(AdminTab.Matrix) }
        NavItem("Modifiers", current == AdminTab.Modifiers) { onSelect(AdminTab.Modifiers) }
        NavItem("Orders", current == AdminTab.Orders) { onSelect(AdminTab.Orders) }
        NavItem("Users", current == AdminTab.Users) { onSelect(AdminTab.Users) }
    }
}

@Composable
private fun NavItem(text: String, active: Boolean, onClick: () -> Unit) {
    Button(attrs = {
        attr("type", "button")
        style {
            width(100.percent)
            property("text-align", "start")
            padding(12.px, 14.px)
            borderRadius(12.px)
            border(0.px, LineStyle.None, Border)
            if (active) {
                property("background", PrimaryGradient)
                color(org.jetbrains.compose.web.css.Color.white)
                property("box-shadow", "0 8px 18px rgba(93, 95, 239, 0.28)")
            } else {
                backgroundColor(org.jetbrains.compose.web.css.Color("#00000000"))
                color(Slate700)
            }
            fontWeight(if (active) "700" else "600")
            fontSize(13.px)
            property("cursor", "pointer")
        }
        onClick { onClick() }
    }) { Text(text) }
}

@Composable
private fun PrimaryButton(text: String, enabled: Boolean = true, onClick: () -> Unit) {
    Button(attrs = {
        attr("type", "button")
        style {
            padding(10.px, 18.px)
            borderRadius(12.px)
            border(0.px, LineStyle.None, Border)
            property("background", if (enabled) PrimaryGradient else "#C7CBEB")
            color(org.jetbrains.compose.web.css.Color.white)
            fontWeight("700")
            fontSize(13.px)
            property("cursor", if (enabled) "pointer" else "not-allowed")
            if (enabled) property("box-shadow", "0 6px 16px rgba(93, 95, 239, 0.28)")
            property("opacity", if (enabled) "1" else "0.7")
        }
        if (!enabled) disabled()
        onClick { onClick() }
    }) { Text(text) }
}

@Composable
private fun GhostButton(text: String, enabled: Boolean = true, onClick: () -> Unit) {
    Button(attrs = {
        attr("type", "button")
        style {
            padding(8.px, 14.px)
            borderRadius(10.px)
            border(1.px, LineStyle.Solid, Border)
            backgroundColor(Surface)
            color(Slate700)
            fontWeight("600")
            fontSize(13.px)
            property("cursor", if (enabled) "pointer" else "not-allowed")
            property("opacity", if (enabled) "1" else "0.6")
        }
        if (!enabled) disabled()
        onClick { onClick() }
    }) { Text(text) }
}

/** Admin /admin/products API: ACTIVE vs INACTIVE (older responses may still say ARCHIVED). */
private fun normalizedAdminProductStatus(status: String): String =
    status.trim().uppercase()

private fun productListRowIsActive(status: String): Boolean =
    normalizedAdminProductStatus(status) == "ACTIVE"

private fun productListStatusLabel(status: String): String = when (normalizedAdminProductStatus(status)) {
    "ACTIVE" -> "Active"
    "ARCHIVED", "INACTIVE" -> "Inactive"
    else -> status.trim().ifBlank { "—" }
}

@Composable
private fun SurfaceCard(content: @Composable () -> Unit) {
    Div({
        style {
            border(1.px, LineStyle.Solid, Slate200)
            borderRadius(16.px)
            backgroundColor(Surface)
            padding(18.px)
            marginBottom(16.px)
            property("box-shadow", SoftCardShadow)
        }
    }) { content() }
}

private fun currentPageAllSelectedForBulk(state: AdminUiState): Boolean {
    val products = state.products
    if (products.isEmpty()) return false
    val sel = state.selectedProductIdsForBulk
    return products.all { canonicalBulkProductId(it.productId) in sel }
}

private fun catalogSectionIsTokaSpareParts(section: CatalogSectionDto): Boolean {
    val key = listOf(section.code, section.name.orEmpty())
        .joinToString(" ")
        .trim()
        .lowercase()
        .replace("_", " ")
        .replace("-", " ")
    return "spare parts" in key
}

private fun createProductBrandOptional(state: AdminUiState): Boolean =
    state.catalogSectionsForCreate.firstOrNull {
        it.id.equals(state.createProductForm.catalogSectionId, ignoreCase = true)
    }?.let(::catalogSectionIsTokaSpareParts) == true

private fun createProductCastOptional(state: AdminUiState): Boolean =
    createProductBrandOptional(state)

@Composable
private fun VariantsSearchTab(state: AdminUiState, vm: AdminViewModel) {
    Div({ style { display(DisplayStyle.Flex); gap(10.px); marginBottom(14.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap") } }) {
        Input(InputType.Text, attrs = {
            value(state.variantSearchQuery)
            placeholder("Type to search variant or product name / SKU")
            onInput { vm.setVariantSearchQuery(it.value) }
            style {
                width(320.px)
                property("max-width", "100%")
                padding(10.px)
                borderRadius(10.px)
                border(1.px, LineStyle.Solid, Border)
            }
        })
        GhostButton("Clear", enabled = !state.loading && state.variantSearchQuery.isNotBlank()) {
            vm.clearVariantSearch()
        }
        GhostButton("Back to Products") { vm.setTab(AdminTab.Products) }
    }
    P({ style { color(Slate500); fontSize(13.px); marginBottom(12.px) } }) {
        Text(
            if (state.variantSearchQuery.isBlank()) {
                "Showing all variants. Type to filter by variant name, product name, or SKU."
            } else {
                "Live results for “${state.variantSearchQuery.trim()}”. Clear to show all variants."
            }
        )
    }
    PaginationRow(
        page = state.variantSearchPage,
        pageSize = state.variantSearchPageSize,
        total = state.variantSearchTotal,
        onPrev = { vm.searchVariantsPage(state.variantSearchPage - 1) },
        onNext = { vm.searchVariantsPage(state.variantSearchPage + 1) },
        onPage = { vm.searchVariantsPage(it) },
    )
    SurfaceCard {
        Div({ style { property("overflow-x", "auto") } }) {
            Table({ style { width(100.percent); property("border-collapse", "collapse"); minWidth(780.px) } }) {
                Thead {
                    Tr {
                        listOf("Variant", "SKU", "Stock", "Product", "Line / Section", "Action").forEach { h ->
                            Th({
                                style {
                                    padding(10.px)
                                    fontSize(11.px)
                                    color(Slate500)
                                    property("text-align", "start")
                                    property("border-bottom", "1px solid #E2E8F0")
                                    fontWeight("700")
                                }
                            }) { Text(h.uppercase()) }
                        }
                    }
                }
                Tbody {
                    state.variantSearchResults.forEach { row ->
                        Tr {
                            Td(attrs = tdCell()) {
                                val variantLabel = row.displayName.trim().ifBlank { row.skuCode }
                                val product = row.productName.trim()
                                val heading =
                                    if (product.isEmpty()) variantLabel else "$product - $variantLabel"
                                ProductNameBlock(heading, row.nameUr)
                            }
                            Td(attrs = tdCell(muted = true)) { Text(row.skuCode.ifBlank { "—" }) }
                            Td(attrs = tdCell(muted = true)) { Text(row.stock.toString()) }
                            Td(attrs = tdCell()) {
                                ProductNameBlock(row.productName, row.productNameUr)
                            }
                            Td(attrs = tdCell(muted = true)) {
                                Text("${row.lineName} · ${row.sectionName}")
                            }
                            Td(attrs = tdCell()) {
                                GhostButton("Open product", enabled = !state.loading) {
                                    vm.openProductFromVariantSearch(row.productId)
                                }
                            }
                        }
                    }
                }
            }
        }
        if (state.variantSearchHasQueried && state.variantSearchResults.isEmpty() && !state.loading) {
            P({ style { color(Slate500); marginTop(12.px); fontSize(13.px) } }) {
                Text(
                    if (state.variantSearchQuery.isBlank()) {
                        "No variants in the catalog."
                    } else {
                        "No variants found for “${state.variantSearchQuery.trim()}”."
                    }
                )
            }
        }
    }
}

@Composable
private fun ProductsTab(state: AdminUiState, vm: AdminViewModel) {
    Div({ style { display(DisplayStyle.Flex); gap(10.px); marginBottom(14.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap") } }) {
        PrimaryButton("Create product") { vm.toggleCreateProductModal(true) }
    }

    H4({
        style {
            color(Slate900)
            margin(0.px)
            marginBottom(8.px)
            fontSize(15.px)
            fontWeight("800")
        }
    }) { Text("Bulk selection & status") }

    Div({
        style {
            display(DisplayStyle.Flex)
            gap(10.px)
            marginBottom(16.px)
            alignItems(AlignItems.Center)
            property("flex-wrap", "wrap")
            padding(14.px)
            borderRadius(10.px)
            border(2.px, LineStyle.Solid, Primary)
            backgroundColor(org.jetbrains.compose.web.css.Color("#EFF6FF"))
            property("width", "100%")
            property("box-sizing", "border-box")
        }
    }) {
        P({
            style {
                margin(0.px)
                fontSize(13.px)
                color(Slate700)
                property("flex", "1 1 240px")
                property("min-width", "200px")
            }
        }) {
            val n = state.selectedProductIdsForBulk.size
            Text(
                if (n == 0) {
                    "Bulk: select rows or use “Select all on this page”. Selection is kept when you change pages."
                } else {
                    "$n product(s) selected (across pages). “Clear page” removes only this page from the selection."
                }
            )
        }
        GhostButton("Select all on this page") { vm.selectAllProductsOnCurrentPage() }
        GhostButton("Clear page from selection") { vm.deselectAllProductsOnCurrentPage() }
        GhostButton("Clear all") { vm.clearBulkProductSelection() }
        PrimaryButton("Activate selected", enabled = state.selectedProductIdsForBulk.isNotEmpty() && !state.saving) {
            vm.bulkActivateSelected()
        }
        PrimaryButton("Deactivate selected", enabled = state.selectedProductIdsForBulk.isNotEmpty() && !state.saving) {
            vm.bulkArchiveSelected()
        }
    }

    if (state.showCreateProductModal) {
        Div({ style { marginBottom(12.px) } }) { CreateProductModal(state, vm) }
    }
    if (state.showEditProductModal) {
        Div({ style { marginBottom(12.px) } }) { EditProductModal(state, vm) }
    }

    Div({ style { display(DisplayStyle.Flex); gap(10.px); marginBottom(14.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap") } }) {
        Input(InputType.Text, attrs = {
            value(state.searchQuery)
            placeholder("Type to search product or variant name / SKU")
            onInput { vm.setSearch(it.value) }
            style {
                width(280.px)
                property("max-width", "100%")
                padding(10.px)
                borderRadius(10.px)
                border(1.px, LineStyle.Solid, Border)
            }
        })
        GhostButton("Clear", enabled = !state.loading && state.searchQuery.isNotBlank()) {
            vm.clearProductSearch()
        }
        GhostButton("Variants search →") { vm.setTab(AdminTab.Variants) }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            gap(10.px)
            marginBottom(14.px)
            alignItems(AlignItems.Center)
            property("flex-wrap", "wrap")
        }
    }) {
        Input(InputType.Text, attrs = {
            value(state.filterSize)
            placeholder("Filter size")
            onInput { vm.updateProductFilters(size = it.value) }
            style {
                width(160.px)
                padding(10.px)
                borderRadius(10.px)
                border(1.px, LineStyle.Solid, Border)
            }
        })
        Input(InputType.Text, attrs = {
            value(state.filterMechanism)
            placeholder("Filter mechanism")
            onInput { vm.updateProductFilters(mechanism = it.value) }
            style {
                width(180.px)
                padding(10.px)
                borderRadius(10.px)
                border(1.px, LineStyle.Solid, Border)
            }
        })
        Input(InputType.Text, attrs = {
            value(state.filterGrade)
            placeholder("Filter grade")
            onInput { vm.updateProductFilters(grade = it.value) }
            style {
                width(160.px)
                padding(10.px)
                borderRadius(10.px)
                border(1.px, LineStyle.Solid, Border)
            }
        })
        Input(InputType.Text, attrs = {
            value(state.filterBrand)
            placeholder("Filter brand")
            onInput { vm.updateProductFilters(brand = it.value) }
            style {
                width(160.px)
                padding(10.px)
                borderRadius(10.px)
                border(1.px, LineStyle.Solid, Border)
            }
        })
        Input(InputType.Text, attrs = {
            value(state.filterCast)
            placeholder("Filter cast")
            onInput { vm.updateProductFilters(cast = it.value) }
            style {
                width(160.px)
                padding(10.px)
                borderRadius(10.px)
                border(1.px, LineStyle.Solid, Border)
            }
        })
        GhostButton("Apply filters") { vm.applyProductSearch() }
        GhostButton("Clear filters") { vm.clearProductFilters() }
    }

    PaginationRow(
        page = state.productsPage,
        pageSize = state.productsPageSize,
        total = state.productsTotal,
        onPrev = { vm.loadProductsPage(state.productsPage - 1) },
        onNext = { vm.loadProductsPage(state.productsPage + 1) },
        onPage = { vm.loadProductsPage(it) }
    )

    P({
        style {
            color(Slate500)
            fontSize(12.px)
            marginBottom(10.px)
            marginTop(0.px)
        }
    }) {
        Text("Product (Urdu) appears in the column immediately after the English name. Scroll horizontally if needed; — when Urdu is not set.")
    }

    SurfaceCard {
        Div({
            style {
                width(100.percent)
                property("max-width", "100%")
                property("overflow-x", "auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            Table({
                style {
                    width(100.percent)
                    property("min-width", "1400px")
                    property("border-collapse", "collapse")
                    property("table-layout", "fixed")
                }
            }) {
                Thead {
                    Tr {
                        Th({
                            style {
                                padding(10.px)
                                color(Slate500)
                                fontSize(11.px)
                                property("text-align", "center")
                                property("border-bottom", "1px solid #E2E8F0")
                                fontWeight("700")
                                property("min-width", "52px")
                                width(4.percent)
                            }
                        }) {
                            val allOnPage = currentPageAllSelectedForBulk(state)
                            key(
                                state.productsPage,
                                state.products.size,
                                allOnPage,
                                state.selectedProductIdsForBulk,
                            ) {
                                CheckboxInput(checked = allOnPage, attrs = {
                                    onChange {
                                        if (currentPageAllSelectedForBulk(state)) {
                                            vm.deselectAllProductsOnCurrentPage()
                                        } else {
                                            vm.selectAllProductsOnCurrentPage()
                                        }
                                    }
                                    style {
                                        width(18.px)
                                        height(18.px)
                                        property("cursor", "pointer")
                                        property("vertical-align", "middle")
                                    }
                                })
                            }
                        }
                        listOf(
                            Triple("IMAGE", "72px", 6),
                            Triple("PRODUCT", "200px", 16),
                            Triple("PRODUCT (URDU)", "220px", 18),
                            Triple("LINE", "96px", 10),
                            Triple("SECTION", "120px", 11),
                            Triple("VARIANTS", "72px", 7),
                            Triple("STATUS", "88px", 8),
                            Triple("ACTIONS", "200px", 15)
                        ).forEach { (label, minW, wPct) ->
                            Th({
                                style {
                                    padding(10.px)
                                    color(Slate500)
                                    fontSize(11.px)
                                    property("text-align", "start")
                                    property("border-bottom", "1px solid #E2E8F0")
                                    fontWeight("700")
                                    property("min-width", minW)
                                    width(wPct.percent)
                                }
                            }) { Text(label) }
                        }
                    }
                }
                Tbody {
                    key(
                        state.selectedProductIdsForBulk,
                        state.productsPage,
                        state.products,
                    ) {
                        for (p in state.products) {
                            val pid = canonicalBulkProductId(p.productId)
                            val rowSelected = pid in state.selectedProductIdsForBulk
                            Tr {
                            Td(attrs = tdCell()) {
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        property("justify-content", "center")
                                        property("align-items", "center")
                                    }
                                }) {
                                    CheckboxInput(checked = rowSelected, attrs = {
                                        onChange { vm.toggleProductBulkSelection(p.productId) }
                                        style {
                                            width(18.px)
                                            height(18.px)
                                            property("cursor", "pointer")
                                            property("vertical-align", "middle")
                                        }
                                    })
                                }
                            }
                            Td(attrs = tdCell()) {
                                val src = resolveProductImageSrc(p.imageUrl)
                                if (src != null) {
                                    Img(src = src, attrs = {
                                        style {
                                            width(48.px)
                                            height(48.px)
                                            borderRadius(8.px)
                                            border(1.px, LineStyle.Solid, Border)
                                            property("object-fit", "cover")
                                            property("display", "block")
                                            backgroundColor(org.jetbrains.compose.web.css.Color("#F8FAFC"))
                                        }
                                    })
                                } else {
                                    Div({
                                        style {
                                            width(48.px)
                                            height(48.px)
                                            borderRadius(8.px)
                                            border(1.px, LineStyle.Solid, Border)
                                            backgroundColor(org.jetbrains.compose.web.css.Color("#F8FAFC"))
                                            display(DisplayStyle.Flex)
                                            alignItems(AlignItems.Center)
                                            property("justify-content", "center")
                                            color(Slate400)
                                            fontSize(11.px)
                                            fontWeight("600")
                                        }
                                    }) { Text("—") }
                                }
                            }
                            Td(attrs = tdCell()) {
                                Div({
                                    style {
                                        property("min-width", "200px")
                                        property("word-wrap", "break-word")
                                    }
                                }) { Text(p.productName) }
                            }
                            Td(attrs = tdCell(muted = true)) {
                                Div({
                                    style {
                                        property("min-width", "200px")
                                        property("max-width", "320px")
                                        property("word-wrap", "break-word")
                                        property("vertical-align", "top")
                                    }
                                }) {
                                    val ur = p.productNameUr?.trim()?.takeIf { it.isNotEmpty() }
                                    if (ur != null) {
                                        Div({
                                            style {
                                                fontSize(13.px)
                                                color(Slate700)
                                                property("direction", "rtl")
                                                property("text-align", "right")
                                                property("font-family", "'Noto Naskh Urdu', 'Segoe UI', 'Arial', sans-serif")
                                            }
                                        }) { Text(ur) }
                                    } else {
                                        Span({ style { color(Slate400); fontSize(12.px) } }) { Text("—") }
                                    }
                                }
                            }
                            Td(attrs = tdCell(muted = true)) { Text(p.lineName) }
                            Td(attrs = tdCell(muted = true)) { Text(p.sectionName) }
                            Td(attrs = tdCell(muted = true)) { Text(p.variantCount.toString()) }
                            Td(attrs = tdCell()) {
                                Span({
                                    style {
                                        backgroundColor(if (productListRowIsActive(p.status)) SuccessBg else org.jetbrains.compose.web.css.Color("#FEE2E2"))
                                        color(if (productListRowIsActive(p.status)) org.jetbrains.compose.web.css.Color("#166534") else Danger)
                                        borderRadius(999.px)
                                        padding(4.px, 10.px)
                                        fontWeight("700")
                                        fontSize(11.px)
                                    }
                                }) { Text(productListStatusLabel(p.status)) }
                            }
                            Td(attrs = tdCell()) {
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        flexDirection(FlexDirection.Column)
                                        gap(6.px)
                                        alignItems(AlignItems.FlexStart)
                                    }
                                }) {
                                    GhostButton("Edit") { vm.openEditProduct(p) }
                                    GhostButton("Variants") { vm.openVariants(p.productId) }
                                    when (normalizedAdminProductStatus(p.status)) {
                                        "ACTIVE" -> GhostButton("Inactive") { vm.archiveProduct(p.productId) }
                                        else -> GhostButton("Active") { vm.activateProduct(p.productId) }
                                    }
                                }
                            }
                            Td(attrs = tdCell()) {
                                ProductListImage(p.imageUrl, p.productName)
                            }
                        }
                        if (state.selectedProductId == p.productId) {
                            Tr {
                                Td(attrs = {
                                    colspan(9)
                                    style {
                                        padding(12.px)
                                        property("border-bottom", "1px solid #E2E8F0")
                                        backgroundColor(org.jetbrains.compose.web.css.Color("#F8FAFC"))
                                    }
                                }) {
                                    ProductVariantsPanel(state, vm, p.productName, p.productNameUr)
                                }
                            }
                        }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ProductListImage(imageUrl: String?, productName: String) {
    val url = resolveProductImageUrl(imageUrl)
    if (url == null) {
        Div({
            style {
                width(56.px)
                height(56.px)
                borderRadius(10.px)
                backgroundColor(org.jetbrains.compose.web.css.Color("#F1F5F9"))
                border(1.px, LineStyle.Solid, Slate200)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("justify-content", "center")
                color(Slate400)
                fontSize(11.px)
                fontWeight("700")
            }
        }) { Text("No img") }
        return
    }

    Img(src = url, attrs = {
        attr("alt", productName)
        style {
            width(56.px)
            height(56.px)
            borderRadius(10.px)
            border(1.px, LineStyle.Solid, Slate200)
            backgroundColor(org.jetbrains.compose.web.css.Color("#F8FAFC"))
            property("object-fit", "cover")
            property("display", "block")
        }
    })
}

private fun resolveProductImageUrl(imageUrl: String?): String? {
    val raw = imageUrl?.trim()?.takeIf { it.isNotEmpty() } ?: return null
    val mediaPath = "/media/products/"
    if (
        (raw.startsWith("http://127.0.0.1", ignoreCase = true) ||
            raw.startsWith("http://localhost", ignoreCase = true)) &&
        raw.contains(mediaPath)
    ) {
        return adminApiBaseUrlForImages() + raw.substringAfter(raw.substringBefore(mediaPath))
    }
    if (
        raw.startsWith("http://", ignoreCase = true) ||
        raw.startsWith("https://", ignoreCase = true) ||
        raw.startsWith("//") ||
        raw.startsWith("data:", ignoreCase = true) ||
        raw.startsWith("blob:", ignoreCase = true)
    ) {
        return raw
    }
    val base = adminApiBaseUrlForImages()
    return if (raw.startsWith("/")) "$base$raw" else "$base/$raw"
}

private fun adminApiBaseUrlForImages(): String {
    val configured = runCatching { js("globalThis.ADMIN_API_BASE_URL") as? String }.getOrNull()
        ?.trim()
        ?.trimEnd('/')
    if (!configured.isNullOrEmpty()) return configured

    val host = window.location.hostname
    val port = window.location.port
    val protocol = window.location.protocol
    if ((host == "localhost" || host == "127.0.0.1") && port != "8080") {
        return "$protocol//$host:8080"
    }
    return "$protocol//${window.location.host}".trimEnd('/')
}

@Composable
private fun ProductNameBlock(productName: String, productNameUr: String?) {
    Div {
        Text(productName)
        val ur = productNameUr?.trim()?.takeIf { it.isNotEmpty() }
        if (ur != null) {
            P({
                style {
                    margin(4.px, 0.px, 0.px, 0.px)
                    color(Slate500)
                    fontSize(12.px)
                    property("font-weight", "500")
                }
            }) { Text(ur) }
        }
    }
}

@Composable
private fun ProductVariantsPanel(state: AdminUiState, vm: AdminViewModel, productName: String, productNameUr: String?) {
    Div {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("justify-content", "space-between")
                marginBottom(10.px)
            }
        }) {
            Div {
                H4({ style { color(Slate900); margin(0.px); marginBottom(4.px) } }) { Text("Variants") }
                ProductNameBlock(productName, productNameUr)
            }
            GhostButton("Close") { vm.closeVariants() }
        }
        when {
            state.variants.isEmpty() && state.loading ->
                P({ style { color(Slate500); margin(0.px); fontSize(13.px) } }) { Text("Loading variants…") }
            state.variants.isEmpty() ->
                P({ style { color(Slate500); margin(0.px); fontSize(13.px) } }) { Text("No variants for this product.") }
            else -> {
                val totalV = state.variants.size
                val ps = state.variantPageSize
                val pages = kotlin.math.max(1, (totalV + ps - 1) / ps)
                val p = state.variantPage.coerceIn(1, pages)
                val slice = state.variants.drop((p - 1) * ps).take(ps)
                PaginationRow(
                    page = p,
                    pageSize = ps,
                    total = totalV.toLong(),
                    onPrev = { vm.setVariantPage(p - 1) },
                    onNext = { vm.setVariantPage(p + 1) },
                    onPage = { vm.setVariantPage(it) }
                )
                Div({
                    style {
                        backgroundColor(Surface)
                        border(1.px, LineStyle.Solid, Border)
                        borderRadius(10.px)
                        padding(10.px)
                        marginTop(8.px)
                    }
                }) {
                    slice.forEach { v -> VariantRow(v, vm, productName) }
                }
            }
        }
    }
}

private fun tdCell(muted: Boolean = false): AttrsScope<HTMLTableCellElement>.() -> Unit = {
    style {
        padding(10.px)
        property("border-bottom", "1px solid #E2E8F0")
        color(if (muted) Slate700 else Slate900)
        fontSize(13.px)
    }
}

/** Prefer API [VariantDetailDto.displayName]; fall back to a specs-based short label. */
private fun simplifiedVariantLabel(v: VariantDetailDto): String {
    v.displayName?.trim()?.takeIf { it.isNotEmpty() }?.let { return it }
    fun pretty(raw: String?): String? {
        val value = raw?.trim()?.takeIf { it.isNotEmpty() } ?: return null
        if (value.matches(Regex("^\\d+(\\.\\d+)?$"))) return value
        return when (value.lowercase().replace('-', '_').replace(' ', '_')) {
            "washer" -> "Washer"
            "bearing" -> "Bearing"
            "sb" -> "SB"
            "oil_box" -> "Oil box"
            "bearing_box" -> "Bearing box"
            "heavy" -> "Heavy"
            "super" -> "Super"
            "awami" -> "Awami"
            "cast_iron", "iron", "ci", "castiron" -> "Iron"
            "steel", "toka_steel" -> "Steel"
            "hand", "toka_hand", "hand_toka" -> "Hand"
            else ->
                value.replace('_', ' ')
                    .split(Regex("\\s+"))
                    .filter { it.isNotEmpty() }
                    .joinToString(" ") { part ->
                        part.replaceFirstChar { ch -> ch.uppercase() }
                    }
        }
    }
    fun spec(vararg keys: String): String? {
        for (key in keys) {
            v.specs[key]?.trim()?.takeIf { it.isNotEmpty() }?.let { return it }
            val lower = key.lowercase()
            v.specs.entries.firstOrNull { it.key.lowercase() == lower }
                ?.value?.trim()?.takeIf { it.isNotEmpty() }
                ?.let { return it }
        }
        return null
    }
    val size = spec("size", "diameter", "dia")
    val wheel = spec("wheel_size", "wheel")
    val sizePart = when {
        !size.isNullOrBlank() && !wheel.isNullOrBlank() -> "$size / W${wheel.trim()}"
        !size.isNullOrBlank() -> size
        else -> null
    }
    val parts = listOfNotNull(
        sizePart,
        pretty(spec("mechanism", "mech", "drive", "drive_mechanism")),
        pretty(spec("grade", "material_grade")),
        pretty(spec("cast")),
    )
    return parts.joinToString(" · ").ifBlank { v.skuCode }
}

@Composable
private fun VariantRow(v: VariantDetailDto, vm: AdminViewModel, productName: String) {
    val legacy = remember(v.variantId) { mutableStateOf(v.legacyId ?: "") }
    val stock = remember(v.variantId) { mutableStateOf(v.stock.toString()) }
    val nameUr = remember(v.variantId, v.nameUr) { mutableStateOf(v.nameUr?.trim().orEmpty()) }
    val variantLabel = remember(v.variantId, v.displayName, v.skuCode, v.specs) {
        simplifiedVariantLabel(v)
    }
    val title = remember(productName, variantLabel) {
        val product = productName.trim()
        if (product.isEmpty()) variantLabel else "$product - $variantLabel"
    }
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
            padding(10.px)
            border(1.px, LineStyle.Solid, if (v.isActive) Border else org.jetbrains.compose.web.css.Color("#FECACA"))
            borderRadius(10.px)
            marginBottom(8.px)
            if (!v.isActive) backgroundColor(org.jetbrains.compose.web.css.Color("#FFF7F7"))
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                property("flex-wrap", "wrap")
            }
        }) {
            Span({
                style {
                    color(Slate900)
                    fontWeight("700")
                    fontSize(13.px)
                    property("line-height", "1.35")
                    property("word-break", "break-word")
                }
            }) { Text(title) }
            Span({
                style {
                    backgroundColor(if (v.isActive) SuccessBg else org.jetbrains.compose.web.css.Color("#FEE2E2"))
                    color(if (v.isActive) org.jetbrains.compose.web.css.Color("#166534") else Danger)
                    borderRadius(999.px)
                    padding(2.px, 8.px)
                    fontWeight("700")
                    fontSize(10.px)
                }
            }) { Text(if (v.isActive) "Active" else "Inactive") }
        }
        if (variantLabel != v.skuCode) {
            Span({
                style {
                    color(Slate500)
                    fontSize(11.px)
                    fontWeight("500")
                    property("word-break", "break-all")
                }
            }) { Text("SKU: ${v.skuCode}") }
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(4.px)
                property("width", "100%")
            }
        }) {
            Span({ style { fontSize(11.px); color(Slate500); fontWeight("600") } }) { Text("Variant name (Urdu)") }
            Input(InputType.Text, attrs = {
                value(nameUr.value)
                onInput { nameUr.value = it.value }
                placeholder("اردو نام")
                style {
                    property("box-sizing", "border-box")
                    width(100.percent)
                    padding(8.px)
                    border(1.px, LineStyle.Solid, Border)
                    borderRadius(8.px)
                    fontSize(13.px)
                    color(Slate900)
                    property("font-family", "'Noto Naskh Urdu', 'Segoe UI', sans-serif")
                }
            })
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                alignItems(AlignItems.Center)
                property("flex-wrap", "wrap")
            }
        }) {
            Input(InputType.Text, attrs = {
                value(legacy.value)
                onInput { legacy.value = it.value }
                placeholder("Legacy ID")
                style { padding(8.px); border(1.px, LineStyle.Solid, Border); borderRadius(8.px); width(140.px) }
            })
            Input(InputType.Text, attrs = {
                value(stock.value)
                onInput { stock.value = it.value }
                placeholder("Stock")
                style { width(72.px); padding(8.px); border(1.px, LineStyle.Solid, Border); borderRadius(8.px) }
            })
            PrimaryButton("Save") {
                vm.patchVariant(
                    v.variantId,
                    legacy.value.ifBlank { null },
                    stock.value.toIntOrNull(),
                    nameUr.value
                )
            }
            if (v.isActive) {
                GhostButton("Inactive") { vm.setVariantActive(v.variantId, false) }
            } else {
                GhostButton("Active") { vm.setVariantActive(v.variantId, true) }
            }
            GhostButton("Delete") { vm.deleteVariant(v.variantId) }
        }
    }
}

@Composable
private fun CreateProductModal(state: AdminUiState, vm: AdminViewModel) {
    val formReady = !state.createProductLoading
    val lineOpts =
        listOf("" to "Select product line…") + state.productLinesOptions.map { it.id to it.name }
    val sectionOpts =
        listOf("" to "Select catalog section…") +
            state.catalogSectionsForCreate.map { s ->
                val title = s.name?.takeIf { it.isNotBlank() } ?: s.code
                s.id to "$title (${s.code})"
            }
    SurfaceCard {
        H4({ style { color(Slate900); marginBottom(8.px) } }) { Text("Create product") }
        if (state.createProductLoading) {
            P({ style { color(Slate500) } }) { Text("Loading options…") }
        } else {
            LabeledInput("Name (English)", state.createProductForm.name) { vm.updateCreateProductForm(name = it) }
            LabeledInput("نام (اردو)", state.createProductForm.nameUr, rtl = true) { vm.updateCreateProductForm(nameUr = it) }
            ProductImageField(
                label = "Product image",
                existingImageUrl = null,
                pendingFile = state.createProductForm.pendingImageFile,
                enabled = formReady && !state.saving,
                onFileSelected = vm::setCreateProductImageFile,
            )
            Div({ style { display(DisplayStyle.Flex); gap(16.px); marginBottom(12.px); property("flex-wrap", "wrap") } }) {
                Div({ style { flex(1); property("min-width", "140px") } }) {
                    LabeledInput("Base price (ST)", state.createProductForm.basePrice) { vm.updateCreateProductForm(basePrice = it) }
                }
                Div({ style { flex(1); property("min-width", "140px") } }) {
                    LabeledInput("Cost", state.createProductForm.costPrice) { vm.updateCreateProductForm(costPrice = it) }
                }
            }
            Div({ style { display(DisplayStyle.Flex); gap(16.px); marginBottom(12.px); property("flex-wrap", "wrap") } }) {
                Div({ style { flex(1); property("min-width", "180px") } }) {
                    LabeledSelect("Product line", state.createProductForm.productLineId, lineOpts, formReady) { vm.setCreateProductLine(it) }
                }
                Div({ style { flex(1); property("min-width", "180px") } }) {
                    LabeledSelect(
                        "Catalog section",
                        state.createProductForm.catalogSectionId,
                        sectionOpts,
                        formReady && state.createProductForm.productLineId.isNotBlank()
                    ) { vm.updateCreateProductForm(catalogSectionId = it) }
                }
            }
            val castOpts =
                state.castOptionsForCreate.map { it.id to "${it.code} – ${it.name}" }
            val brandOpts =
                state.brandOptionsForCreate.map { it.code to "${it.code} – ${it.name ?: it.code}" }
            Div({ style { display(DisplayStyle.Flex); gap(16.px); marginBottom(12.px); property("flex-wrap", "wrap") } }) {
                Div({ style { flex(1) } }) {
                    val lineReady = state.createProductForm.productLineId.isNotBlank()
                    val castPickEnabled = formReady && lineReady
                    val castOptional = createProductCastOptional(state)
                    var newCastCode by remember(state.showCreateProductModal, state.createProductForm.productLineId, state.inlineCastFormNonce) {
                        mutableStateOf("")
                    }
                    var newCastName by remember(state.showCreateProductModal, state.createProductForm.productLineId, state.inlineCastFormNonce) {
                        mutableStateOf("")
                    }
                    var newCastNameUr by remember(state.showCreateProductModal, state.createProductForm.productLineId, state.inlineCastFormNonce) {
                        mutableStateOf("")
                    }
                    CreateProductIdMultiSelect(
                        if (castOptional) "Cast (optional for spare parts)" else "Cast",
                        castOpts,
                        state.createProductForm.selectedCastIds,
                        castPickEnabled,
                        vm::addCreateProductCast,
                        vm::removeCreateProductCast
                    )
                    P({
                        style {
                            fontSize(11.px)
                            color(Slate500)
                            marginTop((-6).px)
                            marginBottom(8.px)
                        }
                    }) {
                        Text(
                            if (castOptional) {
                                "Optional for Toka spare parts. Pick an existing cast, or create one below if needed."
                            } else {
                                "Pick an existing cast, or create one below (saved for this product line)."
                            }
                        )
                    }
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(12.px)
                            property("flex-wrap", "wrap")
                            marginBottom(8.px)
                        }
                    }) {
                        Div({ style { flex(1); property("min-width", "140px") } }) {
                            LabeledInput("New cast code", newCastCode) { newCastCode = it }
                        }
                        Div({ style { flex(1); property("min-width", "140px") } }) {
                            LabeledInput("New cast name (English)", newCastName) { newCastName = it }
                        }
                    }
                    LabeledInput("New cast name (Urdu, optional)", newCastNameUr, rtl = true) {
                        newCastNameUr = it
                    }
                    PrimaryButton(
                        if (state.castCreateBusy) "Saving…" else "Save new cast & select",
                        enabled = castPickEnabled && !state.castCreateBusy && !state.saving
                    ) {
                        vm.createCastForCurrentProductLine(newCastCode, newCastName, newCastNameUr)
                    }
                }
                Div({ style { flex(1) } }) {
                    val brandOptional = createProductBrandOptional(state)
                    CreateProductIdMultiSelect(
                        if (brandOptional) "Brand (optional for spare parts)" else "Brand",
                        brandOpts,
                        state.createProductForm.selectedBrandCodes,
                        formReady,
                        vm::addCreateProductBrand,
                        vm::removeCreateProductBrand
                    )
                    if (brandOptional) {
                        P({
                            style {
                                fontSize(11.px)
                                color(Slate500)
                                marginTop((-6).px)
                                marginBottom(8.px)
                            }
                        }) { Text("Optional for Toka spare parts. Leave empty to use the default product setup.") }
                    }
                }
            }
            Div({ style { display(DisplayStyle.Flex); gap(16.px); marginBottom(12.px); property("flex-wrap", "wrap") } }) {
                Div({ style { flex(1) } }) {
                    CreateProductMultiSelect(
                        "Wheel size",
                        state.wheelPickerOptions,
                        state.createProductForm.selectedWheelSizes,
                        formReady,
                        vm::addCreateProductWheel,
                        vm::removeCreateProductWheel,
                        newValuePlaceholder = "Type wheel size (e.g. 12, 600mm)",
                    )
                }
                Div({ style { flex(1) } }) {
                    CreateProductMultiSelect(
                        "Grade",
                        state.gradePickerOptions,
                        state.createProductForm.selectedGrades,
                        formReady,
                        vm::addCreateProductGrade,
                        vm::removeCreateProductGrade
                    )
                }
            }
            Div({ style { display(DisplayStyle.Flex); gap(16.px); property("flex-wrap", "wrap") } }) {
                Div({ style { flex(1) } }) {
                    CreateProductMultiSelect(
                        "Sizes",
                        state.sizePickerOptions,
                        state.createProductForm.selectedSizes,
                        formReady,
                        vm::addCreateProductSize,
                        vm::removeCreateProductSize
                    )
                }
                Div({ style { flex(1) } }) {
                    CreateProductMultiSelect(
                        "Mechanisms",
                        state.mechanismPickerOptions,
                        state.createProductForm.selectedMechanisms,
                        formReady,
                        vm::addCreateProductMechanism,
                        vm::removeCreateProductMechanism
                    )
                }
            }
        }
        Div({ style { display(DisplayStyle.Flex); gap(10.px); marginTop(16.px) } }) {
            PrimaryButton("Create", enabled = formReady && !state.saving) { vm.createProduct() }
            GhostButton("Cancel") { vm.toggleCreateProductModal(false) }
        }
    }
}

@Composable
private fun EditProductModal(state: AdminUiState, vm: AdminViewModel) {
    val formReady = !state.editProductLoading
    val lineOpts =
        listOf("" to "Select product line…") + state.productLinesOptions.map { it.id to it.name }
    val sectionOpts =
        listOf("" to "Select catalog section…") +
            state.catalogSectionsForCreate.map { s ->
                val title = s.name?.takeIf { it.isNotBlank() } ?: s.code
                s.id to "$title (${s.code})"
            }
    SurfaceCard {
        H4({ style { color(Slate900); marginBottom(8.px) } }) { Text("Edit product") }
        if (state.editProductLoading) {
            P({ style { color(Slate500) } }) { Text("Loading…") }
        } else {
            LabeledInput("Name (English)", state.editProductForm.name) { vm.updateEditProductForm(name = it) }
            LabeledInput("نام (اردو)", state.editProductForm.nameUr, rtl = true) { vm.updateEditProductForm(nameUr = it) }
            ProductImageField(
                label = "Product image",
                existingImageUrl = state.editProductForm.imageUrl,
                pendingFile = state.editProductForm.pendingImageFile,
                enabled = formReady && !state.saving,
                onFileSelected = vm::setEditProductImageFile,
                clearRequested = state.editProductForm.clearImage,
            )
            Div({ style { display(DisplayStyle.Flex); gap(16.px); marginBottom(12.px); property("flex-wrap", "wrap") } }) {
                Div({ style { flex(1); property("min-width", "180px") } }) {
                    LabeledSelect("Product line", state.editProductForm.productLineId, lineOpts, formReady) {
                        vm.setEditProductLine(it)
                    }
                }
                Div({ style { flex(1); property("min-width", "180px") } }) {
                    LabeledSelect(
                        "Catalog section",
                        state.editProductForm.catalogSectionId,
                        sectionOpts,
                        formReady && state.editProductForm.productLineId.isNotBlank()
                    ) { vm.updateEditProductForm(catalogSectionId = it) }
                }
            }
            P({
                style {
                    color(Slate500)
                    fontSize(12.px)
                    marginTop(0.px)
                    marginBottom(12.px)
                }
            }) { Text("Changing line or section affects where this product appears in the catalog. Variants and prices are unchanged.") }

            val editCastOpts = state.castOptionsForCreate.map { it.id to "${it.code} – ${it.name}" }
            val editBrandOpts = state.brandOptionsForCreate.map { it.code to "${it.code} – ${it.name ?: it.code}" }

            Div({ style { display(DisplayStyle.Flex); gap(16.px); marginBottom(12.px); property("flex-wrap", "wrap") } }) {
                Div({ style { flex(1) } }) {
                    CreateProductIdMultiSelect(
                        "Cast",
                        editCastOpts,
                        state.editProductForm.selectedCastIds,
                        formReady,
                        vm::addEditProductCast,
                        vm::removeEditProductCast
                    )
                }
                Div({ style { flex(1) } }) {
                    CreateProductIdMultiSelect(
                        "Brand",
                        editBrandOpts,
                        state.editProductForm.selectedBrandCodes,
                        formReady,
                        vm::addEditProductBrand,
                        vm::removeEditProductBrand
                    )
                }
            }
            Div({ style { display(DisplayStyle.Flex); gap(16.px); marginBottom(12.px); property("flex-wrap", "wrap") } }) {
                Div({ style { flex(1) } }) {
                    CreateProductMultiSelect(
                        "Wheel size",
                        state.wheelPickerOptions,
                        state.editProductForm.selectedWheelSizes,
                        formReady,
                        vm::addEditProductWheel,
                        vm::removeEditProductWheel,
                        newValuePlaceholder = "Type wheel size (e.g. 12, 600mm)",
                    )
                }
                Div({ style { flex(1) } }) {
                    CreateProductMultiSelect(
                        "Grade",
                        state.gradePickerOptions,
                        state.editProductForm.selectedGrades,
                        formReady,
                        vm::addEditProductGrade,
                        vm::removeEditProductGrade
                    )
                }
            }
            Div({ style { display(DisplayStyle.Flex); gap(16.px); marginBottom(12.px); property("flex-wrap", "wrap") } }) {
                Div({ style { flex(1) } }) {
                    CreateProductMultiSelect(
                        "Sizes",
                        state.sizePickerOptions,
                        state.editProductForm.selectedSizes,
                        formReady,
                        vm::addEditProductSize,
                        vm::removeEditProductSize
                    )
                }
                Div({ style { flex(1) } }) {
                    CreateProductMultiSelect(
                        "Mechanisms",
                        state.mechanismPickerOptions,
                        state.editProductForm.selectedMechanisms,
                        formReady,
                        vm::addEditProductMechanism,
                        vm::removeEditProductMechanism
                    )
                }
            }
            P({
                style {
                    color(Slate500)
                    fontSize(11.px)
                    marginTop(0.px)
                    marginBottom(8.px)
                }
            }) { Text("Save adds missing variant combinations and deactivates variants for removed sizes / mechanisms / grades / wheels (and brands / casts when those lists are set).") }
        }
        Div({ style { display(DisplayStyle.Flex); gap(10.px); marginTop(16.px) } }) {
            PrimaryButton("Save", enabled = formReady && !state.saving) { vm.saveEditedProduct() }
            GhostButton("Cancel") { vm.toggleEditProductModal(false) }
        }
    }
}

@Composable
private fun MatrixTab(state: AdminUiState, vm: AdminViewModel) {
    val brands = listOf("ST", "C", "D", "GEN", "N", "S", "T")
    if (state.matrixEditDraft != null) {
        MatrixRowEditModal(state, vm)
    }
    PaginationRow(
        page = state.matrixPage,
        pageSize = state.matrixPageSize,
        total = state.matrixTotal,
        onPrev = { vm.loadMatrixPage(state.matrixPage - 1) },
        onNext = { vm.loadMatrixPage(state.matrixPage + 1) },
        onPage = { vm.loadMatrixPage(it) }
    )
    SurfaceCard {
        Div({ style { display(DisplayStyle.Flex); gap(8.px); alignItems(AlignItems.Center); marginBottom(14.px); property("flex-wrap", "wrap") } }) {
            Span({ style { color(Slate700); fontWeight("700"); fontSize(13.px) } }) { Text("Bulk (this page)") }
            Select(attrs = { onChange { vm.setBulkBrand(it.value ?: "ST") }; style { padding(8.px); borderRadius(8.px); border(1.px, LineStyle.Solid, Border) } }) {
                brands.forEach { Option(value = it) { Text(it) } }
            }
            Select(attrs = { onChange { vm.setBulkType(if (it.value == "percentage") AdjustmentType.Percentage else AdjustmentType.Absolute) }; style { padding(8.px); borderRadius(8.px); border(1.px, LineStyle.Solid, Border) } }) {
                Option("absolute") { Text("Absolute") }
                Option("percentage") { Text("Percentage") }
            }
            Input(InputType.Text, attrs = {
                value(state.bulkAdjustment.amount)
                placeholder("Amount")
                onInput { vm.setBulkAmount(it.value) }
                style { width(100.px); padding(8.px); borderRadius(8.px); border(1.px, LineStyle.Solid, Border) }
            })
            PrimaryButton("Apply bulk") { vm.applyBulkAdjustment(state.bulkAdjustment.brandCode, state.bulkAdjustment.type, state.bulkAdjustment.amount) }
        }

        Div({ style { property("overflow-x", "auto") } }) {
            Table({ style { width(100.percent); property("min-width", "960px") } }) {
                Thead {
                    Tr {
                        listOf("Actions", "Product", "SKU", "ST", "C", "D", "GEN", "N", "S", "T").forEach { h ->
                            Th({
                                style {
                                    padding(8.px)
                                    color(Slate500)
                                    fontSize(11.px)
                                    property("border-bottom", "1px solid #E2E8F0")
                                    fontWeight("700")
                                    if (h == "Actions") {
                                        property("width", "88px")
                                        property("min-width", "88px")
                                        property("white-space", "nowrap")
                                        property("position", "sticky")
                                        property("left", "0")
                                        backgroundColor(Surface)
                                        property("z-index", "3")
                                        property("box-shadow", "4px 0 12px -4px rgba(15,23,42,0.15)")
                                    }
                                }
                            }) { Text(h) }
                        }
                    }
                }
                Tbody {
                    state.matrix.forEach { row ->
                        Tr {
                            Td({
                                style {
                                    padding(10.px)
                                    property("border-bottom", "1px solid #E2E8F0")
                                    property("vertical-align", "middle")
                                    property("width", "88px")
                                    property("min-width", "88px")
                                    property("white-space", "nowrap")
                                    property("position", "sticky")
                                    property("left", "0")
                                    backgroundColor(Surface)
                                    property("z-index", "2")
                                    property("box-shadow", "4px 0 12px -4px rgba(15,23,42,0.12)")
                                }
                            }) {
                                Button(attrs = {
                                    style {
                                        padding(8.px, 12.px)
                                        borderRadius(8.px)
                                        border(1.px, LineStyle.Solid, Primary)
                                        backgroundColor(Surface)
                                        color(Primary)
                                        fontWeight("700")
                                        fontSize(12.px)
                                        property("cursor", "pointer")
                                    }
                                    onClick { vm.openMatrixRowEditor(row) }
                                }) { Text("Edit") }
                            }
                            Td(attrs = tdCell()) { ProductNameBlock(row.productName, row.productNameUr) }
                            Td(attrs = tdCell(muted = true)) { Text(row.baseSku) }
                            listOf("ST", "C", "D", "GEN", "N", "S", "T").forEach { brand ->
                                val value = when (brand) {
                                    "ST" -> row.st
                                    "C" -> row.c
                                    "D" -> row.d
                                    "GEN" -> row.gen
                                    "N" -> row.n
                                    "S" -> row.s
                                    else -> row.t
                                }
                                Td({
                                    style {
                                        padding(6.px)
                                        property("border-bottom", "1px solid #E2E8F0")
                                    }
                                }) {
                                    if (value == null || value == "0" || value == "0.00") {
                                        Span({ style { color(Danger); fontWeight("700"); fontSize(11.px) } }) { Text("—") }
                                    } else {
                                        Input(InputType.Text, attrs = {
                                            value(value)
                                            onChange { vm.updateMatrixCell(row.productId, brand, it.value) }
                                            style {
                                                width(76.px)
                                                padding(6.px)
                                                border(1.px, LineStyle.Solid, Border)
                                                borderRadius(6.px)
                                                fontSize(12.px)
                                            }
                                        })
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun MatrixRowEditModal(state: AdminUiState, vm: AdminViewModel) {
    val d = state.matrixEditDraft ?: return
    Div({
        style {
            property("position", "fixed")
            property("inset", "0")
            property("background-color", "rgba(15,23,42,0.5)")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            property("justify-content", "center")
            property("z-index", "1000")
            padding(20.px)
        }
    }) {
        Div({
            style {
                backgroundColor(Surface)
                borderRadius(16.px)
                padding(28.px)
                width(520.px)
                property("max-width", "100%")
                property("max-height", "92vh")
                property("overflow-y", "auto")
                border(1.px, LineStyle.Solid, Border)
                property("box-shadow", "0 25px 50px -12px rgba(15,23,42,0.25)")
            }
        }) {
            H3({
                style {
                    color(Slate900)
                    margin(0.px)
                    marginBottom(6.px)
                    fontSize(20.px)
                }
            }) { Text("Edit pricing") }
            Div({
                style {
                    color(Slate500)
                    margin(0.px)
                    marginBottom(20.px)
                    fontSize(13.px)
                }
            }) {
                ProductNameBlock(d.productName, d.productNameUr)
            }
            P({
                style {
                    color(Slate700)
                    margin(0.px)
                    marginBottom(16.px)
                    fontSize(12.px)
                    fontWeight("600")
                }
            }) { Text("SKU: ${d.baseSku}") }
            P({
                style {
                    fontSize(12.px)
                    color(Slate500)
                    marginBottom(14.px)
                }
            }) {
                Text("Leave a field empty to keep the current database value for that brand. Use 0 only if you intend to clear the price (allowed with server override).")
            }
            Div({ style { display(DisplayStyle.Flex); property("flex-wrap", "wrap"); gap(12.px); marginBottom(8.px) } }) {
                MatrixModalPriceField("ST", d.st) { vm.updateMatrixEditDraft(st = it) }
                MatrixModalPriceField("C", d.c) { vm.updateMatrixEditDraft(c = it) }
                MatrixModalPriceField("D", d.d) { vm.updateMatrixEditDraft(brandD = it) }
                MatrixModalPriceField("GEN", d.gen) { vm.updateMatrixEditDraft(gen = it) }
                MatrixModalPriceField("N", d.n) { vm.updateMatrixEditDraft(n = it) }
                MatrixModalPriceField("S", d.s) { vm.updateMatrixEditDraft(s = it) }
                MatrixModalPriceField("T", d.t) { vm.updateMatrixEditDraft(t = it) }
            }
            Div({ style { display(DisplayStyle.Flex); gap(10.px); marginTop(20.px); property("flex-wrap", "wrap") } }) {
                PrimaryButton("Save prices", enabled = !state.saving) { vm.saveMatrixRowEditor() }
                GhostButton("Cancel", enabled = !state.saving) { vm.closeMatrixRowEditor() }
            }
        }
    }
}

@Composable
private fun MatrixModalPriceField(label: String, value: String, onValue: (String) -> Unit) {
    Div({ style { width(150.px); property("min-width", "120px"); marginBottom(4.px) } }) {
        P({ style { marginBottom(4.px); fontSize(11.px); fontWeight("700"); color(Slate700) } }) { Text(label) }
        Input(InputType.Text, attrs = {
            value(value)
            onInput { onValue(it.value) }
            placeholder("unchanged")
            style {
                width(100.percent)
                padding(10.px)
                border(1.px, LineStyle.Solid, Border)
                borderRadius(8.px)
                fontSize(14.px)
                color(Slate900)
            }
        })
    }
}

@Composable
private fun ModifiersTab(state: AdminUiState, vm: AdminViewModel) {
    if (state.modifiersEndpointMissing) {
        Div({
            style {
                marginBottom(16.px)
                padding(14.px, 16.px)
                borderRadius(12.px)
                backgroundColor(org.jetbrains.compose.web.css.Color("#FEF3C7"))
                border(1.px, LineStyle.Solid, org.jetbrains.compose.web.css.Color("#F59E0B"))
            }
        }) {
            P({
                style {
                    margin(0.px)
                    marginBottom(8.px)
                    color(Slate900)
                    fontSize(14.px)
                    fontWeight("700")
                }
            }) { Text("Modifiers API not available (HTTP 404)") }
            P({
                style {
                    margin(0.px)
                    color(Slate700)
                    fontSize(13.px)
                    property("line-height", "1.5")
                }
            }) {
                Text(
                    "This app tries GET /admin/modifiers, then GET /admin/modifier-list (same server). If both fail, the process on your API URL is " +
                        "not this build: open http://127.0.0.1:8080/ — the first line should be \"TokaFull Ktor API\". " +
                        "Then pwsh -File scripts/restart-toka-api.ps1 -Force or docker compose build api --no-cache && docker compose up -d api."
                )
            }
        }
    }
    PaginationRow(
        page = state.modifiersPage,
        pageSize = state.modifiersPageSize,
        total = state.modifiersTotal,
        onPrev = { vm.loadModifiersPage(state.modifiersPage - 1) },
        onNext = { vm.loadModifiersPage(state.modifiersPage + 1) },
        onPage = { vm.loadModifiersPage(it) }
    )
    Div({
        style {
            display(DisplayStyle.Flex)
            gap(10.px)
            marginBottom(14.px)
            alignItems(AlignItems.Center)
            property("flex-wrap", "wrap")
        }
    }) {
        Input(InputType.Text, attrs = {
            value(state.modifiersSearchQuery)
            placeholder("Search key, value, Urdu, impact, or id")
            onInput { vm.setModifiersSearch(it.value) }
            style {
                width(320.px)
                property("max-width", "100%")
                padding(10.px)
                borderRadius(10.px)
                border(1.px, LineStyle.Solid, Border)
            }
        })
        GhostButton("Search", enabled = !state.loading) { vm.applyModifiersSearch() }
        GhostButton("Clear", enabled = !state.loading && state.modifiersSearchQuery.isNotBlank()) {
            vm.clearModifiersSearch()
        }
    }
    Div({ style { display(DisplayStyle.Flex); gap(20.px); property("flex-wrap", "wrap") } }) {
        SurfaceCard {
            H4({ style { color(Slate900); marginBottom(10.px) } }) { Text("Create modifier") }
            LabeledInput("Attribute key", state.createModifierForm.attrKey) { vm.updateCreateModifierForm(attrKey = it) }
            LabeledInput("Attribute value (English)", state.createModifierForm.attrValue) { vm.updateCreateModifierForm(attrValue = it) }
            LabeledInput("Attribute value (Urdu)", state.createModifierForm.attrValueUr, rtl = true) { vm.updateCreateModifierForm(attrValueUr = it) }
            LabeledInput("Price impact", state.createModifierForm.priceImpact) { vm.updateCreateModifierForm(priceImpact = it) }
            PrimaryButton("Create") { vm.createModifier() }
        }
        SurfaceCard {
            val editing = state.editingModifierId != null
            H4({
                id("modifier-edit-panel")
                style { color(Slate900); marginBottom(10.px) }
            }) {
                Text(if (editing) "Edit modifier" else "Update modifier")
            }
            if (!editing) {
                P({
                    style {
                        margin(0.px)
                        marginBottom(10.px)
                        fontSize(12.px)
                        color(Slate500)
                        property("line-height", "1.45")
                    }
                }) {
                    Text("Click Edit on a row to load values, or enter a modifier ID manually.")
                }
            }
            if (state.updateModifierForm.modifierId.isBlank()) {
                LabeledInput("Modifier ID (UUID)", state.updateModifierForm.modifierId) { vm.updateUpdateModifierForm(modifierId = it) }
            } else {
                P({
                    style {
                        margin(0.px)
                        marginBottom(10.px)
                        fontSize(12.px)
                        color(Slate700)
                        property("word-break", "break-all")
                    }
                }) { Text("Modifier ID: ${state.updateModifierForm.modifierId}") }
            }
            LabeledInput("Attribute key", state.updateModifierForm.attrKey) { vm.updateUpdateModifierForm(attrKey = it) }
            LabeledInput("Attribute value (English)", state.updateModifierForm.attrValue) { vm.updateUpdateModifierForm(attrValue = it) }
            LabeledInput("Attribute value (Urdu)", state.updateModifierForm.attrValueUr, rtl = true) { vm.updateUpdateModifierForm(attrValueUr = it) }
            LabeledInput("Price impact", state.updateModifierForm.priceImpact) { vm.updateUpdateModifierForm(priceImpact = it) }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(10.px)
                    property("flex-wrap", "wrap")
                    marginTop(8.px)
                }
            }) {
                PrimaryButton("Update", enabled = !state.saving) { vm.patchModifier() }
                if (editing) {
                    GhostButton("Cancel", enabled = !state.saving) { vm.cancelModifierEdit() }
                }
            }
        }
    }
    SurfaceCard {
        H4({ style { color(Slate900); marginBottom(10.px) } }) { Text("Registered modifiers") }
        Table({ style { width(100.percent) } }) {
            Thead {
                Tr {
                    listOf("Key", "Value (EN)", "Value (UR)", "Impact", "ID", "").forEach { h ->
                        Th({ style { padding(8.px); fontSize(11.px); color(Slate500); property("text-align", if (h.isEmpty()) "end" else "start") } }) {
                            Text(h.uppercase().ifBlank { "ACTION" })
                        }
                    }
                }
            }
            Tbody {
                state.modifiers.forEach { m ->
                    val rowSelected = state.editingModifierId?.equals(m.id, ignoreCase = true) == true
                    Tr(attrs = {
                        if (rowSelected) {
                            style { backgroundColor(org.jetbrains.compose.web.css.Color("#EFF6FF")) }
                        }
                    }) {
                        Td(attrs = tdCell()) { Text(m.attrKey) }
                        Td(attrs = tdCell()) { Text(m.attrValue) }
                        Td(attrs = tdCell()) { Text(m.attrValueUr?.ifBlank { "—" } ?: "—") }
                        Td(attrs = tdCell()) { Text(m.priceImpact) }
                        Td(attrs = tdCell(muted = true)) { Text(m.id.take(8) + "…") }
                        Td(attrs = tdCell()) {
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    gap(8.px)
                                    property("justify-content", "flex-end")
                                }
                            }) {
                                GhostButton("Edit", enabled = !state.saving) { vm.openModifierForEdit(m) }
                                GhostButton("Delete", enabled = !state.saving) {
                                    val msg =
                                        "Remove this modifier from the catalog (${m.attrKey}: ${m.attrValue})? " +
                                            "Existing variant specs are unchanged; pricing will no longer add this modifier's impact."
                                    if (window.confirm(msg)) {
                                        vm.deleteModifier(m.id)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun UsersTab(state: AdminUiState, vm: AdminViewModel) {
    val f = state.newUserForm
    if (state.usersEndpointMissing) {
        Div({
            style {
                marginBottom(16.px)
                padding(14.px, 16.px)
                borderRadius(12.px)
                backgroundColor(org.jetbrains.compose.web.css.Color("#FEF3C7"))
                border(1.px, LineStyle.Solid, org.jetbrains.compose.web.css.Color("#F59E0B"))
            }
        }) {
            P({
                style {
                    margin(0.px)
                    marginBottom(8.px)
                    color(Slate900)
                    fontSize(14.px)
                    fontWeight("700")
                }
            }) { Text("Users API not available (HTTP 404)") }
            P({
                style {
                    margin(0.px)
                    color(Slate700)
                    fontSize(13.px)
                    property("line-height", "1.5")
                }
            }) {
                Text(
                    "This app tries GET /admin/users, then GET /admin/user-list (same server). If both fail, rebuild and restart " +
                        "the API (e.g. .\\gradlew.bat :server:run or docker compose build api --no-cache && docker compose up -d api)."
                )
            }
        }
    }
    PaginationRow(
        page = state.usersPage,
        pageSize = state.usersPageSize,
        total = state.usersTotal,
        onPrev = { vm.loadUsersPage(state.usersPage - 1) },
        onNext = { vm.loadUsersPage(state.usersPage + 1) },
        onPage = { vm.loadUsersPage(it) }
    )
    SurfaceCard {
        H4({ style { color(Slate900); marginBottom(12.px) } }) { Text("Create user") }
        P({
            style {
                margin(0.px)
                marginBottom(12.px)
                fontSize(12.px)
                color(Slate500)
                property("line-height", "1.45")
            }
        }) {
            Text("Username and password are required. Choose type App user for mobile-app login (name, address, and mobile required; Urdu name optional). For Admin, profile fields are optional.")
        }
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fill, minmax(200px, 1fr))")
                gap(12.px)
                alignItems(AlignItems.End)
                marginBottom(12.px)
            }
        }) {
            LabeledSelect("User type", f.userType, USER_TYPE_SELECT_OPTIONS, enabled = !state.saving) {
                vm.updateNewUserForm { u -> u.copy(userType = it) }
            }
            LabeledSelect("Status", f.status, USER_STATUS_SELECT_OPTIONS, enabled = !state.saving) {
                vm.updateNewUserForm { u -> u.copy(status = it) }
            }
            LabeledInput("Username *", f.username) { vm.updateNewUserForm { u -> u.copy(username = it) } }
            LabeledInput("Password *", f.password, isPassword = true) { vm.updateNewUserForm { u -> u.copy(password = it) } }
            LabeledInput("Name (English)", f.nameEn) { vm.updateNewUserForm { u -> u.copy(nameEn = it) } }
            LabeledInput("Name (Urdu)", f.nameUr, rtl = true) { vm.updateNewUserForm { u -> u.copy(nameUr = it) } }
            LabeledInput("Mobile", f.mobileEn) { vm.updateNewUserForm { u -> u.copy(mobileEn = it) } }
        }
        Div({ style { marginBottom(12.px) } }) {
            LabeledInput("Address", f.addressEn) { vm.updateNewUserForm { u -> u.copy(addressEn = it) } }
        }
        CreateProductIdMultiSelect(
            label = "Brands (leave empty to block all product access)",
            options = state.brandOptionsForUsers.map { it.code to (it.name ?: it.code) },
            selectedKeys = f.selectedBrandCodes,
            enabled = !state.saving,
            onAdd = { vm.addNewUserBrand(it) },
            onRemove = { vm.removeNewUserBrand(it) },
        )
        Div({ style { height(12.px) } }) {}
        PrimaryButton("Create user", enabled = !state.saving) { vm.createUser() }
    }
    SurfaceCard {
        Div({ style { property("overflow-x", "auto") } }) {
            Table({ style { width(100.percent); property("border-collapse", "collapse"); minWidth(820.px) } }) {
                Thead {
                    Tr {
                        listOf(
                            "Username", "Type", "Status", "Name (EN)", "Name (UR)", "Mobile", "Address", "Created", "Actions"
                        ).forEach { h ->
                            Th({
                                style {
                                    padding(10.px)
                                    fontSize(11.px)
                                    color(Slate500)
                                    property("text-align", "start")
                                    property("border-bottom", "1px solid #E2E8F0")
                                    fontWeight("700")
                                }
                            }) { Text(h.uppercase()) }
                        }
                    }
                }
                Tbody {
                    state.users.forEach { u ->
                        Tr {
                            Td(attrs = tdCell()) { Text(u.username) }
                            Td(attrs = tdCell(muted = true)) { Text(userTypeLabel(u.userType)) }
                            Td(attrs = tdCell()) {
                                UserStatusSelect(
                                    userId = u.id,
                                    currentStatus = u.status,
                                    enabled = !state.saving,
                                    vm = vm
                                )
                            }
                            Td(attrs = tdCell(muted = true)) { Text(truncUserCell(u.nameEn)) }
                            Td(attrs = tdCell(muted = true)) { Text(truncUserCell(u.nameUr)) }
                            Td(attrs = tdCell(muted = true)) { Text(u.mobileEn ?: "—") }
                            Td(attrs = tdCell(muted = true)) { Text(truncUserCell(u.addressEn, 28)) }
                            Td(attrs = tdCell(muted = true)) { Text(formatEpochMs(u.createdAt)) }
                            Td(attrs = tdCell()) {
                                Div({ style { display(DisplayStyle.Flex); gap(8.px); property("flex-wrap", "wrap") } }) {
                                    GhostButton("Edit", enabled = !state.loading) { vm.openUserEditor(u) }
                                    GhostButton("Remove", enabled = !state.saving) { vm.deleteUser(u.id) }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun UserStatusSelect(userId: String, currentStatus: String, enabled: Boolean, vm: AdminViewModel) {
    Select(attrs = {
        attr("value", currentStatus.lowercase())
        onChange { vm.patchUserStatus(userId, it.value ?: currentStatus) }
        if (!enabled) disabled()
        style {
            width(110.px)
            property("max-width", "130px")
            padding(8.px)
            border(1.px, LineStyle.Solid, Border)
            borderRadius(8.px)
            fontSize(12.px)
            color(Slate900)
        }
    }) {
        USER_STATUS_SELECT_OPTIONS.forEach { (v, label) ->
            Option(value = v) { Text(label) }
        }
    }
}

@Composable
private fun UserEditOverlay(form: EditUserFormState, state: AdminUiState, vm: AdminViewModel) {
    val saving = state.saving
    Div({
        style {
            property("position", "fixed")
            property("inset", "0")
            property("z-index", "1000")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            property("justify-content", "center")
            padding(16.px)
            backgroundColor(org.jetbrains.compose.web.css.Color("rgba(15, 23, 42, 0.45)"))
        }
    }) {
        Div({
            style {
                backgroundColor(Surface)
                borderRadius(16.px)
                padding(24.px)
                property("max-width", "640px")
                width(100.percent)
                property("max-height", "90vh")
                property("overflow-y", "auto")
                property("box-shadow", "0 25px 50px rgba(15,23,42,0.15)")
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    property("justify-content", "space-between")
                    alignItems(AlignItems.Center)
                    marginBottom(16.px)
                }
            }) {
                H3({ style { margin(0.px); color(Slate900); fontSize(20.px) } }) { Text("Edit ${form.username}") }
                Div({ style { display(DisplayStyle.Flex); gap(8.px) } }) {
                    PrimaryButton("Save", enabled = !saving) { vm.saveUserEditor() }
                    GhostButton("Close") { vm.closeUserEditor() }
                }
            }
            P({ style { color(Slate500); fontSize(12.px); marginBottom(12.px) } }) {
                Text("Leave new password empty to keep the current password. Agent users must keep name, address, and mobile filled.")
            }
            LabeledSelect("User type", form.userType, USER_TYPE_SELECT_OPTIONS, enabled = !saving) {
                vm.updateEditUserForm { e -> e.copy(userType = it) }
            }
            LabeledSelect("Status", form.status, USER_STATUS_SELECT_OPTIONS, enabled = !saving) {
                vm.updateEditUserForm { e -> e.copy(status = it) }
            }
            LabeledInput("Name (English)", form.nameEn) { vm.updateEditUserForm { e -> e.copy(nameEn = it) } }
            LabeledInput("Name (Urdu)", form.nameUr, rtl = true) { vm.updateEditUserForm { e -> e.copy(nameUr = it) } }
            LabeledInput("Mobile", form.mobileEn) { vm.updateEditUserForm { e -> e.copy(mobileEn = it) } }
            LabeledInput("Address", form.addressEn) { vm.updateEditUserForm { e -> e.copy(addressEn = it) } }
            LabeledInput("New password (optional)", form.passwordNew, isPassword = true) {
                vm.updateEditUserForm { e -> e.copy(passwordNew = it) }
            }
            Div({ style { height(12.px) } }) {}
            CreateProductIdMultiSelect(
                label = "Brands (leave empty to block all product access)",
                options = state.brandOptionsForUsers.map { it.code to (it.name ?: it.code) },
                selectedKeys = form.selectedBrandCodes,
                enabled = !saving,
                onAdd = { vm.addEditUserBrand(it) },
                onRemove = { vm.removeEditUserBrand(it) },
            )
        }
    }
}

@Composable
private fun OrdersTab(state: AdminUiState, vm: AdminViewModel) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
            marginBottom(12.px)
            property("flex-wrap", "wrap")
        }
    }) {
        Span({ style { color(Slate700); fontSize(13.px); fontWeight("700") } }) { Text("Filter by status") }
        Select(attrs = {
            onChange { vm.setOrdersStatusFilter(it.value.orEmpty()) }
            style {
                property("min-width", "160px")
                padding(8.px)
                border(1.px, LineStyle.Solid, Border)
                borderRadius(8.px)
                fontSize(13.px)
                color(Slate900)
            }
        }) {
            Option(value = "", attrs = {
                if (state.ordersStatusFilter.isBlank()) selected()
            }) { Text("All statuses") }
            ADMIN_ORDER_STATUS_OPTIONS.forEach { (v, label) ->
                Option(value = v, attrs = {
                    if (state.ordersStatusFilter == v) selected()
                }) { Text(label) }
            }
        }
        Span({ style { color(Slate500); fontSize(12.px) } }) {
            Text("Showing orders from the mobile app (same sales_orders list).")
        }
    }
    PaginationRow(
        page = state.ordersPage,
        pageSize = state.ordersPageSize,
        total = state.ordersTotal,
        onPrev = { vm.loadOrdersPage(state.ordersPage - 1) },
        onNext = { vm.loadOrdersPage(state.ordersPage + 1) },
        onPage = { vm.loadOrdersPage(it) }
    )
    SurfaceCard {
        Table({ style { width(100.percent); property("border-collapse", "collapse") } }) {
            Thead {
                Tr {
                    listOf("Order ID", "Summary", "Lines", "Brand", "SKU", "Qty", "Total", "Created", "Status", "Print", "Actions").forEach { h ->
                        Th({
                            style {
                                padding(10.px)
                                fontSize(11.px)
                                color(Slate500)
                                property("text-align", "start")
                                property("border-bottom", "1px solid #E2E8F0")
                                fontWeight("700")
                            }
                        }) { Text(h.uppercase()) }
                    }
                }
            }
            Tbody {
                state.orders.forEach { o ->
                    Tr {
                        Td(attrs = tdCell(muted = true)) { Text(o.orderId) }
                        Td(attrs = tdCell()) { ProductNameBlock(o.productName, o.productNameUr) }
                        Td(attrs = tdCell(muted = true)) { Text(o.lineCount.toString()) }
                        Td(attrs = tdCell(muted = true)) { Text(o.brandCode ?: "—") }
                        Td(attrs = tdCell(muted = true)) { Text(o.skuCode ?: "—") }
                        Td(attrs = tdCell(muted = true)) { Text(o.quantity.toString()) }
                        Td(attrs = tdCell()) { Text(o.totalPrice) }
                        Td(attrs = tdCell(muted = true)) { Text(formatEpochMs(o.createdAtMs)) }
                        Td(attrs = tdCell()) {
                            key("${o.orderId}:${o.status}") {
                                OrderStatusSelect(
                                    orderCode = o.orderId,
                                    currentStatus = o.status,
                                    enabled = !state.saving,
                                    vm = vm
                                )
                            }
                        }
                        Td(attrs = tdCell()) {
                            if (o.printCount > 0) {
                                Span({
                                    style {
                                        fontSize(11.px)
                                        fontWeight("700")
                                        color(org.jetbrains.compose.web.css.Color("#92400E"))
                                        backgroundColor(org.jetbrains.compose.web.css.Color("#FEF3C7"))
                                        padding(4.px, 8.px)
                                        borderRadius(999.px)
                                    }
                                }) {
                                    Text("Printed ×${o.printCount}")
                                }
                            } else {
                                Span({ style { color(Slate500); fontSize(12.px) } }) { Text("Not printed") }
                            }
                        }
                        Td(attrs = tdCell()) {
                            GhostButton("View", enabled = !state.loading) { vm.openOrderDetail(o.orderId) }
                        }
                    }
                }
            }
        }
        if (state.orders.isEmpty() && !state.loading) {
            P({ style { color(Slate500); marginTop(12.px); fontSize(13.px) } }) { Text("No orders yet.") }
        }
    }
}

@Composable
private fun OrderStatusSelect(orderCode: String, currentStatus: String, enabled: Boolean, vm: AdminViewModel) {
    val current = currentStatus.trim().lowercase()
    Select(attrs = {
        onChange { ev ->
            val next = ev.value?.trim()?.lowercase().orEmpty()
            if (next.isNotEmpty() && next != current) {
                vm.updateOrderStatus(orderCode, next)
            }
        }
        if (!enabled) disabled()
        style {
            width(130.px)
            property("max-width", "160px")
            padding(8.px)
            border(1.px, LineStyle.Solid, Border)
            borderRadius(8.px)
            fontSize(12.px)
            color(Slate900)
        }
    }) {
        ADMIN_ORDER_STATUS_OPTIONS.forEach { (v, label) ->
            Option(value = v, attrs = {
                if (v == current) selected()
            }) { Text(label) }
        }
    }
}

@Composable
private fun OrderDetailOverlay(detail: OrderDetailDto, vm: AdminViewModel) {
    val initialDates = remember(detail.orderId, detail.estimatedDelivery) {
        parseEstimatedDeliveryToIsoPair(detail.estimatedDelivery)
    }
    var deliveryFromIso by remember(detail.orderId, detail.estimatedDelivery) {
        mutableStateOf(initialDates.first)
    }
    var deliveryToIso by remember(detail.orderId, detail.estimatedDelivery) {
        mutableStateOf(initialDates.second)
    }
    val deliveryDraft = formatEstimatedDeliveryLabel(deliveryFromIso, deliveryToIso)
    Div({
        style {
            property("position", "fixed")
            property("inset", "0")
            property("z-index", "1000")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            property("justify-content", "center")
            padding(16.px)
            backgroundColor(org.jetbrains.compose.web.css.Color("rgba(15, 23, 42, 0.45)"))
            property("overflow-y", "auto")
            property("box-sizing", "border-box")
        }
        // Click backdrop to close (does not close when clicking the panel).
        onClick { vm.closeOrderDetail() }
    }) {
        Div({
            style {
                backgroundColor(Surface)
                borderRadius(16.px)
                property("max-width", "720px")
                width(100.percent)
                property("max-height", "min(92vh, 900px)")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                property("box-shadow", "0 25px 50px rgba(15,23,42,0.15)")
                property("overflow", "hidden")
                property("box-sizing", "border-box")
                property("margin", "auto")
            }
            onClick { it.stopPropagation() }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    property("justify-content", "space-between")
                    alignItems(AlignItems.Center)
                    property("flex-shrink", "0")
                    gap(12.px)
                    padding(20.px, 24.px, 12.px, 24.px)
                    property("border-bottom", "1px solid #E2E8F0")
                    backgroundColor(Surface)
                }
            }) {
                H3({ style { margin(0.px); color(Slate900); fontSize(20.px) } }) { Text("Order ${detail.orderId}") }
                Div({ style { display(DisplayStyle.Flex); gap(8.px); property("flex-shrink", "0") } }) {
                    GhostButton(if (detail.printCount > 0) "Reprint" else "Print") {
                        printOrderFromAdmin(detail, vm)
                    }
                    GhostButton("Close") { vm.closeOrderDetail() }
                }
            }
            Div({
                style {
                    property("overflow-y", "auto")
                    property("flex", "1 1 auto")
                    property("min-height", "0")
                    padding(16.px, 24.px, 24.px, 24.px)
                }
            }) {
                P({ style { color(Slate500); fontSize(12.px); marginBottom(16.px) } }) {
                    Text("Change status from the orders list. Set estimated delivery below for the customer app.")
                }
                if (detail.printCount > 0) {
                    P({
                        style {
                            color(org.jetbrains.compose.web.css.Color("#92400E"))
                            backgroundColor(org.jetbrains.compose.web.css.Color("#FEF3C7"))
                            padding(10.px)
                            borderRadius(8.px)
                            fontSize(13.px)
                            fontWeight("700")
                            marginBottom(12.px)
                        }
                    }) {
                        val last = detail.printedAtMs?.let { formatEpochMs(it) } ?: "—"
                        Text("Already printed ${detail.printCount} time(s). Last print: $last. Use Reprint for another copy.")
                    }
                }
                Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(10.px) } }) {
                    val lines =
                        detail.lines.takeIf { it.isNotEmpty() }
                            ?: listOf(
                                OrderLineItemDto(
                                    productName = detail.productName,
                                    productNameUr = detail.productNameUr,
                                    brandCode = detail.brandCode,
                                    skuCode = detail.skuCode,
                                    quantity = detail.quantity,
                                    totalPrice = detail.totalPrice
                                )
                            )
                    Div({
                        style {
                            property("overflow-x", "auto")
                            property("max-height", "min(48vh, 420px)")
                            property("overflow-y", "auto")
                            border(1.px, LineStyle.Solid, Border)
                            borderRadius(10.px)
                            marginBottom(8.px)
                        }
                    }) {
                        Table({
                            style {
                                width(100.percent)
                                property("border-collapse", "collapse")
                                fontSize(12.px)
                            }
                        }) {
                            Thead {
                                Tr {
                                    listOf("Product", "Brand", "SKU", "Qty", "Line total").forEach { h ->
                                        Th({
                                            style {
                                                padding(8.px)
                                                property("text-align", "start")
                                                color(Slate500)
                                                property("border-bottom", "1px solid #E2E8F0")
                                                fontWeight("700")
                                                property("position", "sticky")
                                                property("top", "0")
                                                backgroundColor(Surface)
                                                property("z-index", "1")
                                            }
                                        }) { Text(h) }
                                    }
                                }
                            }
                            Tbody {
                                lines.forEach { line ->
                                    Tr {
                                        Td({
                                            style {
                                                padding(8.px)
                                                property("vertical-align", "top")
                                                property("border-bottom", "1px solid #F1F5F9")
                                            }
                                        }) { ProductNameBlock(line.productName, line.productNameUr) }
                                        Td(attrs = tdCell(muted = true)) { Text(line.brandCode ?: "—") }
                                        Td(attrs = tdCell(muted = true)) { Text(line.skuCode ?: "—") }
                                        Td(attrs = tdCell(muted = true)) { Text(line.quantity.toString()) }
                                        Td(attrs = tdCell()) { Text(line.totalPrice) }
                                    }
                                }
                            }
                        }
                    }
                    Div({
                        style {
                            padding(10.px)
                            backgroundColor(org.jetbrains.compose.web.css.Color("#F8FAFC"))
                            borderRadius(8.px)
                        }
                    }) {
                        P({ style { margin(0.px); color(Slate500); fontSize(10.px); fontWeight("700") } }) { Text("Agent") }
                        ProductNameBlock(orderAgentNameText(detail), detail.agentNameUr)
                    }
                    OrderDetailRow("Contact", orderAgentContactText(detail))
                    OrderDetailRow("Order total", "${detail.totalPrice} (${detail.quantity} items)")
                    OrderDetailRow(
                        "Status",
                        ADMIN_ORDER_STATUS_OPTIONS.find { it.first == detail.status.lowercase() }?.second ?: detail.status
                    )
                    OrderDetailRow("Created", formatEpochMs(detail.createdAtMs))
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(8.px)
                            padding(10.px)
                            backgroundColor(org.jetbrains.compose.web.css.Color("#F8FAFC"))
                            borderRadius(8.px)
                        }
                    }) {
                        Span({ style { color(Slate500); fontWeight("700"); fontSize(13.px) } }) {
                            Text("Est. delivery")
                        }
                        P({
                            style {
                                margin(0.px)
                                color(Slate500)
                                fontSize(12.px)
                                property("line-height", "1.4")
                            }
                        }) {
                            Text("Pick dates with the calendar. Use From and To for a range, or only From for a single date.")
                        }
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                gap(10.px)
                                property("flex-wrap", "wrap")
                                alignItems(AlignItems.FlexEnd)
                            }
                        }) {
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    gap(4.px)
                                    property("flex", "1 1 160px")
                                    property("min-width", "140px")
                                }
                            }) {
                                Span({ style { fontSize(11.px); color(Slate500); fontWeight("600") } }) { Text("From") }
                                Input(InputType.Date, attrs = {
                                    value(deliveryFromIso)
                                    onInput { deliveryFromIso = it.value }
                                    style {
                                        width(100.percent)
                                        padding(10.px)
                                        border(1.px, LineStyle.Solid, Border)
                                        borderRadius(8.px)
                                        fontSize(13.px)
                                        color(Slate900)
                                        backgroundColor(Surface)
                                    }
                                })
                            }
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    gap(4.px)
                                    property("flex", "1 1 160px")
                                    property("min-width", "140px")
                                }
                            }) {
                                Span({ style { fontSize(11.px); color(Slate500); fontWeight("600") } }) { Text("To (optional)") }
                                Input(InputType.Date, attrs = {
                                    value(deliveryToIso)
                                    if (deliveryFromIso.isNotBlank()) {
                                        attr("min", deliveryFromIso)
                                    }
                                    onInput { deliveryToIso = it.value }
                                    style {
                                        width(100.percent)
                                        padding(10.px)
                                        border(1.px, LineStyle.Solid, Border)
                                        borderRadius(8.px)
                                        fontSize(13.px)
                                        color(Slate900)
                                        backgroundColor(Surface)
                                    }
                                })
                            }
                        }
                        if (deliveryDraft.isNotBlank()) {
                            P({
                                style {
                                    margin(0.px)
                                    fontSize(12.px)
                                    color(Slate700)
                                    fontWeight("600")
                                }
                            }) { Text("Will show in app as: $deliveryDraft") }
                        }
                        Div({ style { display(DisplayStyle.Flex); gap(8.px); property("flex-wrap", "wrap") } }) {
                            PrimaryButton(
                                "Save delivery",
                                enabled = deliveryDraft.trim() != detail.estimatedDelivery.trim(),
                            ) {
                                val from = deliveryFromIso.trim()
                                val to = deliveryToIso.trim()
                                val normalized =
                                    if (from.isNotBlank() && to.isNotBlank() && to < from) {
                                        formatEstimatedDeliveryLabel(to, from)
                                    } else {
                                        deliveryDraft
                                    }
                                vm.updateOrderEstimatedDelivery(detail.orderId, normalized)
                            }
                            GhostButton(
                                "Clear",
                                enabled = detail.estimatedDelivery.isNotBlank() ||
                                    deliveryFromIso.isNotBlank() ||
                                    deliveryToIso.isNotBlank(),
                            ) {
                                deliveryFromIso = ""
                                deliveryToIso = ""
                                vm.updateOrderEstimatedDelivery(detail.orderId, "")
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun OrderDetailRow(label: String, value: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            property("justify-content", "space-between")
            gap(12.px)
            padding(10.px)
            backgroundColor(org.jetbrains.compose.web.css.Color("#F8FAFC"))
            borderRadius(8.px)
            fontSize(13.px)
        }
    }) {
        Span({ style { color(Slate500); fontWeight("700") } }) { Text(label) }
        Span({ style { color(Slate900); property("text-align", "end") } }) { Text(value) }
    }
}

@Composable
private fun AuditTrail(state: AdminUiState) {
    if (state.auditLog.isEmpty()) return
    H4({ style { color(Slate900); marginTop(20.px); marginBottom(8.px) } }) { Text("Activity") }
    SurfaceCard {
        state.auditLog.take(12).forEach { P({ style { color(Slate700); marginBottom(6.px); fontSize(12.px) } }) { Text(it) } }
    }
}

@Composable
private fun LabeledInput(
    label: String,
    value: String,
    rtl: Boolean = false,
    isPassword: Boolean = false,
    onValue: (String) -> Unit,
) {
    Div({ style { marginBottom(12.px) } }) {
        P({
            style {
                marginBottom(4.px)
                fontSize(12.px)
                fontWeight("700")
                color(Slate700)
                if (rtl) property("text-align", "end")
            }
        }) { Text(label) }
        if (isPassword) {
            Input(InputType.Password, attrs = {
                value(value)
                onInput { onValue(it.value) }
                style {
                    width(100.percent)
                    padding(10.px)
                    border(1.px, LineStyle.Solid, Border)
                    borderRadius(8.px)
                    color(Slate900)
                }
            })
        } else {
            Input(InputType.Text, attrs = {
                value(value)
                onInput { onValue(it.value) }
                style {
                    width(100.percent)
                    padding(10.px)
                    border(1.px, LineStyle.Solid, Border)
                    borderRadius(8.px)
                    color(Slate900)
                    if (rtl) {
                        property("direction", "rtl")
                        property("text-align", "right")
                    }
                }
            })
        }
    }
}

@Composable
private fun LabeledSelect(
    label: String,
    selectedValue: String,
    options: List<Pair<String, String>>,
    enabled: Boolean = true,
    onChange: (String) -> Unit
) {
    Div({ style { marginBottom(12.px) } }) {
        P({ style { marginBottom(4.px); fontSize(12.px); fontWeight("700"); color(Slate700) } }) { Text(label) }
        val optionKey = options.joinToString("\u001f") { "${it.first}\u001e${it.second}" }
        key(selectedValue, optionKey, enabled) {
            Select(attrs = {
                onChange { onChange(it.value ?: "") }
                if (!enabled) disabled()
                style {
                    width(100.percent)
                    padding(10.px)
                    border(1.px, LineStyle.Solid, Border)
                    borderRadius(8.px)
                    color(Slate900)
                }
            }) {
                options.forEach { (optValue, optLabel) ->
                    Option(
                        value = optValue,
                        attrs = {
                            if (optValue == selectedValue) {
                                selected()
                            }
                        }
                    ) { Text(optLabel) }
                }
            }
        }
    }
}

@Composable
private fun CreateProductIdMultiSelect(
    label: String,
    options: List<Pair<String, String>>,
    selectedKeys: List<String>,
    enabled: Boolean,
    onAdd: (String) -> Unit,
    onRemove: (String) -> Unit
) {
    var draft by remember(label) { mutableStateOf("") }
    fun labelFor(key: String): String =
        options.firstOrNull { it.first.equals(key, ignoreCase = true) }?.second ?: key
    Div({ style { marginBottom(12.px) } }) {
        P({ style { marginBottom(4.px); fontSize(12.px); fontWeight("700"); color(Slate700) } }) { Text(label) }
        Div({ style { display(DisplayStyle.Flex); gap(8.px); alignItems(AlignItems.Center); marginBottom(6.px) } }) {
            Select(attrs = {
                attr("value", draft)
                onChange { draft = it.value ?: "" }
                if (!enabled) disabled()
                style { flex(1); padding(10.px); border(1.px, LineStyle.Solid, Border); borderRadius(8.px) }
            }) {
                Option(value = "") { Text("Choose…") }
                options.forEach { (optValue, optLabel) ->
                    Option(value = optValue) { Text(optLabel) }
                }
            }
            GhostButton("Add", enabled = enabled && draft.isNotBlank()) {
                onAdd(draft)
                draft = ""
            }
        }
        selectedKeys.forEach { k ->
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px); marginBottom(4.px) } }) {
                Span({ style { fontSize(13.px); color(Slate900) } }) { Text(labelFor(k)) }
                GhostButton("Remove") { onRemove(k) }
            }
        }
    }
}

@Composable
private fun CreateProductMultiSelect(
    label: String,
    options: List<String>,
    selected: List<String>,
    enabled: Boolean,
    onAdd: (String) -> Unit,
    onRemove: (String) -> Unit,
    newValuePlaceholder: String = "Type a value not listed…",
) {
    var draftSelect by remember(label, options.size) { mutableStateOf("") }
    var draftText by remember(label) { mutableStateOf("") }
    fun addFromSuggestion() {
        val pick = draftSelect.trim()
        if (pick.isBlank()) return
        onAdd(pick)
        draftSelect = ""
    }
    fun addTyped() {
        val pick = draftText.trim()
        if (pick.isBlank()) return
        onAdd(pick)
        draftText = ""
    }
    Div({ style { marginBottom(12.px) } }) {
        P({ style { marginBottom(4.px); fontSize(12.px); fontWeight("700"); color(Slate700) } }) { Text(label) }
        P({
            style {
                marginBottom(6.px)
                fontSize(11.px)
                color(Slate500)
            }
        }) { Text("Dropdown lists suggestions from existing data; use the box below to enter any value.") }
        Div({ style { display(DisplayStyle.Flex); gap(8.px); alignItems(AlignItems.Center); marginBottom(6.px) } }) {
            Select(attrs = {
                attr("value", draftSelect)
                onChange { draftSelect = it.value ?: "" }
                if (!enabled) disabled()
                style { flex(1); padding(10.px); border(1.px, LineStyle.Solid, Border); borderRadius(8.px) }
            }) {
                Option(value = "") { Text("Suggestions…") }
                options.filter { it !in selected }.forEach { opt -> Option(value = opt) { Text(opt) } }
            }
            GhostButton("Add", enabled = enabled && draftSelect.isNotBlank()) { addFromSuggestion() }
        }
        Div({ style { display(DisplayStyle.Flex); gap(8.px); alignItems(AlignItems.Center); marginBottom(6.px) } }) {
            Input(InputType.Text, attrs = {
                value(draftText)
                onInput { draftText = it.value }
                placeholder(newValuePlaceholder)
                if (!enabled) disabled()
                style {
                    flex(1)
                    padding(10.px)
                    border(1.px, LineStyle.Solid, Border)
                    borderRadius(8.px)
                    color(Slate900)
                }
            })
            GhostButton("Add", enabled = enabled && draftText.isNotBlank()) { addTyped() }
        }
        selected.forEach { v ->
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px); marginBottom(4.px) } }) {
                Span({ style { fontSize(13.px); color(Slate900) } }) { Text(v) }
                GhostButton("Remove") { onRemove(v) }
            }
        }
    }
}
